import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, Pressable, ScrollView, StyleSheet } from 'react-native';
import {
  X,
  Link,
  Minus,
  Circle,
  Square,
  House,
  ChevronDown,
  ChevronRight,
  Check,
  Plus,
  Copy,
  Trash2,
  Bell,
  Download,
  Pencil,
  MoreHorizontal,
  Maximize2,
  Globe,
  Lock,
  type LucideIcon,
} from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  ELEVATION,
  PLAN_PANEL_HEADER_HEIGHT,
  withWeight,
} from '../../theme';
import { PLAN_PANEL_WIDTH } from '../../theme/layout';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { Tooltip } from '../common/Tooltip';
import { add as addUndo } from '../../utils/undoGroup';
import { computeFeatureLabels } from '../../utils/planFeatureLabels';
import {
  useDefensePlansStore,
  defaultNewPlanTitle,
  unpublishedAssignmentUpdates,
} from '../../store/defensePlansStore';
import { isEscapeLayerOpen, useEscapeToClose } from '../../utils/escapeStack';
import { bundleFundingText, bundleIdsKeptByFunding, isLiveFundingRequest } from '../../utils/money';
import { useUIStore } from '../../store/uiStore';
import { usePlanEventsStore } from '../../store/planEventsStore';
import {
  bundleIdsWithUpcomingEvents,
  nextEventForBundle,
  upcomingEventsForBundle,
} from '../../utils/eventCalendar';
import { usePlanListDrag } from '../../hooks/usePlanListDrag.web';
import { dropHighlightKey, resolvePlanDrop } from '../../utils/planListDrop';
import { MAX_BUNDLES_PER_PLAN } from '../../schemas/firestoreSchemas';
import { PlanDragGhost } from './PlanDragGhost.web';
import { PlanFeatureContextMenu } from './PlanFeatureContextMenu.web';
import { useAuthStore } from '../../store/authStore';
import { shareBundleOpportunity, sharePlan } from '../../utils/share';
import { CompanionPanel, useCompanionPlacement } from '../layout/CompanionPanel';
import { Crossfade } from '../common/Crossfade';
import { AnimatedHeight } from '../common/AnimatedHeight';
import { useJustDrawnFeatureIds } from '../../hooks/useJustDrawnFeatureIds';
import { Skeleton } from '../common/Skeleton';
import { usePanelShown } from '../../utils/panelShown';
import { useLeavingPanel } from '../../hooks/useLeavingPanel';
import { usePanelHistoryEntry } from '../../hooks/usePanelHistory';
// Imported from their own files, not the `../common` barrel: the barrel
// also re-exports ContentActionMenu, which pulls in @gorhom/bottom-sheet ->
// react-native-reanimated's native worklets init — fine at runtime, but it
// crashes this component's jest render test, which has no reason to load
// any of that for a plain button and a text link. ConfirmDialog's own
// module has no such dependency (Modal + Button + escapeStack only), so
// useConfirm is safe to pull in the same direct way.
import { Button } from '../common/Button';
import { Popover } from '../common/Popover';
import { SegmentedControl } from '../common/SegmentedControl';
import { TextLink } from '../common/TextLink';
import { useConfirm } from '../common/ConfirmDialog';
import {
  DEFENSE_STRATEGIES,
  DEFENSE_STRATEGY_ORDER,
  STRATEGY_GROUPS,
  kindOptionsForStrategy,
  strategyBadgeColors,
} from './defenseStrategies';
import { DefensePlanFeatureRow, BundleListMenu } from './DefensePlanFeatureRow.web';
import { useRevealSelectedPlanRow } from './useRevealSelectedPlanRow.web';
import { DefensePlanBundleGroup } from './DefensePlanBundleGroup.web';
import { DefensePlanBundleRow, BUNDLE_COLLAPSE_THRESHOLD } from './DefensePlanBundleRow.web';
import { DefensePlanAssignSheet, type AssignBundleTarget } from './DefensePlanAssignSheet.web';
import { DefensePlanAssignForm, assignTargetLabel } from './DefensePlanAssignForm.web';
import { DefensePlanPartyEditSheet } from './DefensePlanPartyEditSheet.web';
import { DefensePlanPartyEditForm } from './DefensePlanPartyEditForm.web';
import { PlanUpdatesSheet } from './PlanUpdatesSheet.web';
import { PlanUpdatesView } from './PlanUpdatesView.web';
import { PlanEventsSheet } from './PlanEventsSheet.web';
import { PlanEventsPanel } from './PlanEventsPanel.web';
import { DefensePlanBidsSheet } from './DefensePlanBidsSheet.web';
import { DefensePlanVendorsSheet } from './DefensePlanVendorsSheet.web';
import { DefensePlanBidsView } from './DefensePlanBidsView.web';
import { BundleVendorsView } from './BundleVendorsView.web';
import { partyDisplayName } from './planParties';
import { PlanPartiesSection } from './PlanPartiesSection.web';
import { buildPlanNeedsCsv, planNeedsFilename } from '@utils/planNeedsCsv';
import { downloadText, isDownloadSupported } from '@utils/downloadFile';
import {
  summarizeAssignments,
  bundleRowWhoText,
  linksForFeature,
} from '../../utils/planAssignmentSummary';
import { formatFeatureMeta } from '../../utils/planFeatureFormat';
import { anonWritesBlocked } from '../../constants/auth';
import {
  orderFeaturesNewestFirst,
  groupFeaturesForList,
  defaultBundleName,
  visibleFeatureIds,
  effectiveBundleTeam,
  effectiveBundleLeader,
  bundleIdsKeptByBids,
} from '../../utils/planBundles';
import { usePlanShortcuts, planShortcutsLive } from '../../hooks/usePlanShortcuts.web';
import { isPlanFeatureSelected, isRangeClick, isToggleClick } from '../../utils/planSelection';
import type {
  DefenseStrategy,
  DefensePlanFeature,
  DefensePlanAssignment,
  PlanVisibility,
  PlanDrawKind,
} from '../../types';

// Content for the plan editor's DockedPanel (mounted by app/(tabs)/index.tsx
// as `<DockedPanel width={PLAN_PANEL_WIDTH} testID="defense-plan-panel">`).
//
// Deliberately named .web.tsx and imported by the screen through that exact
// path (not a platform-agnostic `./DefensePlanPanel`), so Metro resolves it
// only into the web bundle. It talks to zustand stores alone: no leaflet
// import, no map access, nothing that would pull the map's Leaflet
// dependency into the native bundle by association.
//
// Drawing itself is a web-only, mouse-only interaction (useDefensePlanDraw)
// and stays that way on purpose. Touch has no hover, so there is no way to
// show the rubber-band preview segment a click-to-place line needs; and a
// fingertip is wide enough that tapping a vertex onto a roofline becomes a
// guess, not a placement. A touch version is a different interaction to
// design later, not a responsive variant of this one.

// Two columns of list-style tiles, badge beside label (Sept 25 2026). Four
// columns of stacked tiles couldn't pad evenly: a 76px tile holds
// "Active wildfire" only with 2px at the sides, and a one-line label left
// 23px under it against 9px over the badge. Here every tile is the badge
// plus the same padding on all four sides, whatever its label.
const PALETTE_COLUMNS = 2;

// The strategy badge, shared by the grid tiles and the collapsed row.
const STRATEGY_BADGE_SIZE = 28;
// The tile's box: a border that's always there and always drawn (Sept 28:
// Jason preferred the prototype's outlined tiles to borderless ones; it
// turns focus-colored when selected, so no state moves anything) and one
// padding on every side. The collapsed "Fire break · Line" row takes the same box, so its
// badge lands where the tile's was.
const STRATEGY_TILE_BOX = {
  borderWidth: 1,
  borderRadius: BORDER_RADIUS.md,
  padding: SPACING.xs,
} as const;

const KIND_LABEL: Record<PlanDrawKind, string> = {
  line: 'Line',
  point: 'Point',
  area: 'Area',
  home: 'Home',
};
const KIND_ICON: Record<PlanDrawKind, LucideIcon> = {
  line: Minus,
  point: Circle,
  area: Square,
  home: House,
};

const VISIBILITY_OPTIONS: { key: PlanVisibility; label: string }[] = [
  { key: 'shared', label: 'Shared' },
  { key: 'private', label: 'Private' },
];
const VISIBILITY_CAPTION: Record<PlanVisibility, string> = {
  shared: 'Neighbors and anyone with the link can see it.',
  private: 'Only you can see it.',
};

type HintSegment = { text: string } | { key: string };
const HINT_SEGMENTS: Record<'none' | PlanDrawKind, HintSegment[]> = {
  none: [{ text: 'Pick a strategy, then draw on the map.' }],
  point: [{ text: 'Click the map to place it. Keep clicking to add more.' }],
  // One instruction per line, each short enough never to wrap (Sept 28,
  // again Oct 1 once the Backspace cap made the keys line too long): left to
  // wrap on its own, a key cap landed first on a line, and its box pushed
  // it out of line with the text above. So each line starts with a word.
  line: [
    { text: 'Click to add each bend.\nDouble-click or ' },
    { key: 'Enter' },
    { text: '\u00a0to finish.\nPress ' },
    { key: '\u232b' },
    { text: '\u00a0to undo a point, ' },
    { key: 'Esc' },
    { text: '\u00a0to\u00a0cancel.' },
  ],
  area: [
    { text: 'Click each corner.\nDouble-click or ' },
    { key: 'Enter' },
    { text: '\u00a0to close it.\nPress ' },
    { key: '\u232b' },
    { text: '\u00a0to undo a point, ' },
    { key: 'Esc' },
    { text: '\u00a0to\u00a0cancel.' },
  ],
  // Overridden in render: HOME_ZOOM_HINT while property pins aren't visible
  // at the current zoom, or a transient "no home there" while
  // uiStore.planHomeMissId is fresh (see the hintSegments computation
  // below). This is the steady-state text once homes are on screen.
  home: [{ text: 'Click a home on the map to add it. Keep clicking to add more.' }],
};

const HOME_ZOOM_HINT: HintSegment[] = [{ text: 'Zoom in until homes show, then click one.' }];
const TOO_SHORT_HINT: HintSegment[] = [{ text: 'Draw a little longer' }];
const HOME_MISS_HINT: HintSegment[] = [{ text: 'No home there. Try clicking closer to a house.' }];
// How long the "no home there" hint stays up after a home-mode click misses
// \u2014 long enough to read, short enough that it doesn't linger once the user
// has moved on.
const HOME_MISS_HINT_MS = 3000;
// How long "Draw a little longer" stays up.
const TOO_SHORT_HINT_MS = 2000;

// A plan whose last feature was just deleted keeps its doc (see the store's
// removeFeature comment — undo needs the id to still be there), so "does a
// plan exist" alone isn't the right test for "is there anything to show as
// saved" — an empty plan reads exactly like never having drawn one.
function hasDrawnAnything(ownPlan: { features: unknown[] } | null): boolean {
  return !!ownPlan && ownPlan.features.length > 0;
}

function headerMetaText(
  ownPlan: { features: { id: string }[] } | null,
  isSaving: boolean,
  saveError: string | null,
  assignments: Record<string, DefensePlanAssignment>
): string {
  if (saveError) return saveError;
  if (isSaving) return 'Saving…';
  if (!hasDrawnAnything(ownPlan)) return 'Draw your first defense to save this plan.';
  const summary = summarizeAssignments(
    (ownPlan as { features: { id: string }[] }).features,
    assignments
  );
  // "3 defenses, 2 assigned" (the panel-system prototype's header count,
  // Sept 28): what's in the plan and how much of it has someone.
  const noun = summary.total === 1 ? 'defense' : 'defenses';
  const parts = [`${summary.total} ${noun}`, `${summary.haveSomeone} assigned`];
  if (summary.done > 0) parts.push(`${summary.done} done`);
  return `${parts.join(', ')} · Saved`;
}

const VISIBILITY_ICON: Record<PlanVisibility, LucideIcon> = {
  shared: Globe,
  private: Lock,
};

/** Shared/Private as a small pill beside the header's count, opening a
 *  two-item menu that says what each means. Keeps the old toggle's
 *  testIDs (plan-visibility-toggle on the pill, -shared/-private on the
 *  items). */
export function VisibilityPill({
  value,
  onChange,
  initialOpen = false,
}: {
  value: PlanVisibility;
  onChange: (v: PlanVisibility) => void;
  /** Mount with the menu open; only the design-system gallery uses it. */
  initialOpen?: boolean;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const [open, setOpen] = useState(initialOpen);
  const anchorRef = useRef<View>(null);
  const close = useCallback(() => setOpen(false), []);
  const Icon = VISIBILITY_ICON[value];
  const label = VISIBILITY_OPTIONS.find((o) => o.key === value)?.label ?? value;
  return (
    <>
      <Pressable
        ref={anchorRef}
        onPress={() => setOpen((v) => !v)}
        testID="plan-visibility-toggle"
        accessibilityRole="button"
        accessibilityLabel={`Who can see this plan: ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        {...hoverProps}
        style={[
          styles.visibilityPill,
          CURSOR_POINTER,
          {
            borderColor: colors.border,
            backgroundColor: hovered || open ? colors.hoverSurface : 'transparent',
          },
        ]}>
        <Icon size={12} color={colors.textSecondary} />
        <Text style={[styles.visibilityPillLabel, { color: colors.textSecondary }]}>{label}</Text>
        <ChevronDown size={12} color={colors.textTertiary} />
      </Pressable>
      <Popover
        visible={open}
        anchorRef={anchorRef}
        onClose={close}
        placement="bottom-start"
        gap={SPACING.xxs}
        testID="plan-visibility-menu"
        accessibilityLabel="Who can see this plan">
        <View style={styles.visibilityMenu}>
          {VISIBILITY_OPTIONS.map((option) => (
            <VisibilityMenuRow
              key={option.key}
              icon={VISIBILITY_ICON[option.key]}
              label={option.label}
              caption={VISIBILITY_CAPTION[option.key]}
              selected={option.key === value}
              testID={`plan-visibility-toggle-${option.key}`}
              onPress={() => {
                setOpen(false);
                if (option.key !== value) onChange(option.key);
              }}
            />
          ))}
        </View>
      </Popover>
    </>
  );
}

function VisibilityMenuRow({
  icon: Icon,
  label,
  caption,
  selected,
  onPress,
  testID,
}: {
  icon: LucideIcon;
  label: string;
  caption: string;
  selected: boolean;
  onPress: () => void;
  testID: string;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="menuitem"
      aria-checked={selected}
      {...hoverProps}
      style={[
        styles.visibilityMenuRow,
        CURSOR_POINTER,
        { backgroundColor: hovered ? colors.hoverSurface : 'transparent' },
      ]}>
      <Icon size={16} color={colors.textSecondary} />
      <View style={styles.visibilityMenuText}>
        <Text style={[styles.switcherRowTitle, { color: colors.text }]}>{label}</Text>
        <Text style={[styles.switcherRowMeta, { color: colors.textSecondary }]}>{caption}</Text>
      </View>
      {selected && <Check size={16} color={colors.text} />}
    </Pressable>
  );
}

/** One group's tiles: rows of PALETTE_COLUMNS, the last row padded with
 *  invisible spacers that take the tile style, so a short row stays
 *  left-aligned and nothing shifts. */
function PaletteGrid({
  strategies,
  selectedStrategy,
  onPressStrategy,
  showShortcuts,
  labelledBy,
}: {
  strategies: DefenseStrategy[];
  selectedStrategy: DefenseStrategy | undefined;
  onPressStrategy: (strategy: DefenseStrategy) => void;
  /** Show each tile's digit key (desktop shell only). */
  showShortcuts: boolean;
  /** nativeID of the overline that names this group. */
  labelledBy: string;
}) {
  return (
    <View style={styles.palette} accessibilityRole="radiogroup" aria-labelledby={labelledBy}>
      {Array.from({ length: Math.ceil(strategies.length / PALETTE_COLUMNS) }).map((_, row) => {
        const rowStrategies = strategies.slice(
          row * PALETTE_COLUMNS,
          row * PALETTE_COLUMNS + PALETTE_COLUMNS
        );
        const spacerCount = PALETTE_COLUMNS - rowStrategies.length;
        return (
          <View key={row} style={styles.paletteRow}>
            {rowStrategies.map((strategy) => {
              const digit = DEFENSE_STRATEGY_ORDER.indexOf(strategy) + 1;
              return (
                <StrategyTile
                  key={strategy}
                  strategy={strategy}
                  selected={selectedStrategy === strategy}
                  onPress={() => onPressStrategy(strategy)}
                  shortcutNumber={showShortcuts && digit >= 1 && digit <= 9 ? digit : undefined}
                />
              );
            })}
            {Array.from({ length: spacerCount }).map((_, i) => (
              <View
                key={`spacer-${i}`}
                style={[styles.tile, styles.paletteSpacer]}
                pointerEvents="none"
              />
            ))}
          </View>
        );
      })}
    </View>
  );
}

function StrategyTile({
  strategy,
  selected,
  onPress,
  shortcutNumber,
}: {
  strategy: DefenseStrategy;
  selected: boolean;
  onPress: () => void;
  /** The digit key that picks this tile (usePlanShortcuts); omitted where
   *  the keyboard layer is off. */
  shortcutNumber?: number;
}) {
  const { colors, isDark } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const def = DEFENSE_STRATEGIES[strategy];
  const badge = strategyBadgeColors(strategy, isDark);
  // A native browser tooltip (the DOM `title` attribute) showing the
  // strategy's description on hover. React Native Web doesn't forward an
  // unrecognized `title` prop to the DOM (its prop allowlist has no entry
  // for it — see forwardedProps in react-native-web), so it's set
  // imperatively on the underlying element instead, the same way
  // useAutoGrowInput.ts and PresentationFullscreen.tsx reach past RN's
  // props onto the web DOM node a ref resolves to on this platform.
  const tileRef = useRef<View>(null);
  useEffect(() => {
    // Guards a jest render (react-native test renderer, no real DOM node
    // behind the ref) rather than a real runtime condition — this
    // component only ever mounts on web, where the ref always resolves to
    // an element with setAttribute.
    const node = tileRef.current as unknown as HTMLElement | null;
    if (typeof node?.setAttribute !== 'function') return;
    node.setAttribute(
      'title',
      shortcutNumber ? `${def.label} (${shortcutNumber}): ${def.description}` : def.description
    );
  }, [def.description, def.label, shortcutNumber]);
  return (
    <Pressable
      ref={tileRef}
      onPress={onPress}
      testID={`plan-strategy-${strategy}`}
      accessibilityRole="radio"
      aria-checked={selected}
      accessibilityLabel={def.label}
      {...hoverProps}
      style={[
        styles.tile,
        CURSOR_POINTER,
        {
          borderColor: selected ? colors.borderFocus : colors.border,
          backgroundColor: selected || hovered ? colors.hoverSurface : 'transparent',
        },
      ]}>
      <View style={styles.strategyBadge}>
        <def.icon size={16} color={badge.icon} />
      </View>
      <Text style={[styles.tileLabel, { color: colors.text }]} numberOfLines={2}>
        {def.label}
      </Text>
    </Pressable>
  );
}

// The "Add a defense" region's collapsed-with-a-selection state: a single
// row carrying the picked strategy's badge + label + armed kind ("Fire
// break · Line" — this is what tells Glenn he's about to draw an area, not
// a line), with a "Change strategy" link that re-expands the tile grid (see
// DefensePlanPanel's paletteExpanded) and a "Done" link that clears the
// tool outright (see DefensePlanPanel's back-to-the-grid effect). Mirrors
// StrategyTile's badge treatment so the strategy reads the same whether the
// grid is open or closed.
function SelectedStrategyRow({
  strategy,
  kind,
  showKind,
  onChange,
  onDone,
}: {
  strategy: DefenseStrategy;
  kind: PlanDrawKind;
  /** Name the kind in the label only when no Draw-as control says it right
   *  below (a home strategy); otherwise "· Line" repeated the control. */
  showKind: boolean;
  onChange: () => void;
  onDone: () => void;
}) {
  const { colors, isDark } = useTheme();
  const def = DEFENSE_STRATEGIES[strategy];
  const badge = strategyBadgeColors(strategy, isDark);
  // Drawn as the selected tile (focus border, hover fill), so it reads as
  // the tile you picked rather than a new kind of row (Sept 28).
  return (
    <View
      style={[
        styles.selectedStrategyRow,
        { borderColor: colors.borderFocus, backgroundColor: colors.hoverSurface },
      ]}>
      <View style={styles.selectedStrategyLeft}>
        <View style={styles.strategyBadge}>
          <def.icon size={16} color={badge.icon} />
        </View>
        <Text style={[styles.selectedStrategyLabel, { color: colors.text }]} numberOfLines={1}>
          {showKind ? `${def.label} · ${KIND_LABEL[kind]}` : def.label}
        </Text>
      </View>
      <View style={styles.selectedStrategyActions}>
        <TextLink
          label="Change"
          onPress={onChange}
          testID="plan-strategy-change"
          accessibilityLabel="Change strategy"
          textStyle={TYPOGRAPHY.caption}
        />
        <Button
          title="Done"
          onPress={onDone}
          variant="secondary"
          size="xs"
          testID="plan-strategy-done"
          accessibilityLabel="Done adding defenses"
        />
      </View>
    </View>
  );
}

// One row of the "Bundle" picker's flat feature list (select mode — see the
// "Bundle" TextLink below): a checkbox in place of the row's usual delete
// control, otherwise reading the same as DefensePlanFeatureRow. Kept as its
// own lightweight component rather than teaching DefensePlanFeatureRow a
// checkbox mode — only the editor's select mode ever needs one. The list it
// sits in shows loose defenses only (plus the edited bundle's own members);
// a defense in another bundle moves through that bundle's "Add or remove
// defenses", never through this picker.
function BundleSelectRow({
  feature,
  displayLabel,
  checked,
  onToggle,
}: {
  feature: DefensePlanFeature;
  /** "Fire break 2" when the strategy repeats in the plan; see planFeatureLabels. */
  displayLabel?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  const { colors, isDark } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const def = DEFENSE_STRATEGIES[feature.strategy];
  const label = displayLabel ?? def.label;
  const badge = strategyBadgeColors(feature.strategy, isDark);
  return (
    <Pressable
      onPress={onToggle}
      testID={`plan-feature-select-${feature.id}`}
      accessibilityRole="checkbox"
      aria-checked={checked}
      accessibilityLabel={`${label}, ${formatFeatureMeta(feature)}`}
      {...hoverProps}
      style={[
        styles.selectRow,
        CURSOR_POINTER,
        {
          backgroundColor: checked
            ? colors.pressedSurface
            : hovered
              ? colors.hoverSurface
              : 'transparent',
        },
      ]}>
      <View
        style={[
          styles.checkbox,
          {
            borderColor: checked ? colors.action : colors.border,
            backgroundColor: checked ? colors.action : 'transparent',
          },
        ]}>
        {checked && <Check size={12} color={colors.actionOn} />}
      </View>
      <View style={[styles.badge, { backgroundColor: badge.tint }]}>
        <def.icon size={13} color={badge.icon} />
      </View>
      <View style={styles.text}>
        <Text style={[styles.label, { color: colors.text }]} numberOfLines={1}>
          {label}
        </Text>
        <Text style={[styles.meta, { color: colors.textTertiary }]}>
          {formatFeatureMeta(feature)}
        </Text>
      </View>
    </Pressable>
  );
}

export function DefensePlanPanel() {
  const { colors } = useTheme();
  const user = useAuthStore((s) => s.user);
  const ownPlan = useDefensePlansStore((s) => s.ownPlan);
  const ownPlans = useDefensePlansStore((s) => s.ownPlans);
  // Waiting on the first Firestore snapshot of this user's plans: neither
  // "Nothing drawn yet." nor the empty meta line is true until it lands.
  // Only a signed-in, non-anonymous user subscribes (useMapSubscriptions),
  // and a new draft (startNewPlan) is empty by choice, so neither waits.
  const ownPlansLoaded = useDefensePlansStore((s) => s.ownPlansLoaded);
  const draftingNewPlan = useDefensePlansStore((s) => s.draftingNewPlan);
  const planLoading =
    !!user?.id && !user.isAnonymous && !ownPlansLoaded && !draftingNewPlan && ownPlan === null;
  // "Fire break 1", "Fire break 2" when a strategy repeats — the same labels
  // the read-only viewer shows, so a row means the same thing in both.
  const featureLabels = useMemo(
    () => computeFeatureLabels(ownPlan?.features ?? [], (s) => DEFENSE_STRATEGIES[s].label),
    [ownPlan?.features]
  );
  const isSaving = useDefensePlansStore((s) => s.isSaving);
  const saveError = useDefensePlansStore((s) => s.saveError);
  const updateTitle = useDefensePlansStore((s) => s.updateTitle);
  const setVisibility = useDefensePlansStore((s) => s.setVisibility);
  const removeFeature = useDefensePlansStore((s) => s.removeFeature);
  const partiesByPlan = useDefensePlansStore((s) => s.partiesByPlan);
  const assignmentsByPlan = useDefensePlansStore((s) => s.assignmentsByPlan);
  const assignmentUpdatesByPlan = useDefensePlansStore((s) => s.assignmentUpdatesByPlan);
  const planBids = useDefensePlansStore((s) => (ownPlan ? s.bidsByPlan[ownPlan.id] : undefined));
  const planFundingRequests = useDefensePlansStore((s) =>
    ownPlan ? s.fundingRequestsByPlan[ownPlan.id] : undefined
  );
  const setBundleBidsOpen = useDefensePlansStore((s) => s.setBundleBidsOpen);
  const planEvents = usePlanEventsStore((s) => (ownPlan ? s.eventsByPlan[ownPlan.id] : undefined));
  const cancelUpcomingForBundle = usePlanEventsStore((s) => s.cancelUpcomingForBundle);
  const setActiveOwnPlan = useDefensePlansStore((s) => s.setActiveOwnPlan);
  const startNewPlan = useDefensePlansStore((s) => s.startNewPlan);
  const duplicateActivePlan = useDefensePlansStore((s) => s.duplicateActivePlan);
  const deleteOwnPlan = useDefensePlansStore((s) => s.deleteOwnPlan);
  const createBundle = useDefensePlansStore((s) => s.createBundle);
  const renameBundle = useDefensePlansStore((s) => s.renameBundle);
  const ungroupBundle = useDefensePlansStore((s) => s.ungroupBundle);
  const setBundleMembers = useDefensePlansStore((s) => s.setBundleMembers);
  const moveFeaturesToBundle = useDefensePlansStore((s) => s.moveFeaturesToBundle);
  const restoreBundleMembership = useDefensePlansStore((s) => s.restoreBundleMembership);
  const confirm = useConfirm();
  const closePlanPanel = useUIStore((s) => s.closePlanPanel);
  const planDrawTool = useUIStore((s) => s.planDrawTool);
  const setPlanDrawTool = useUIStore((s) => s.setPlanDrawTool);
  const selectedPlanFeatureId = useUIStore((s) => s.selectedPlanFeatureId);
  const setSelectedPlanFeature = useUIStore((s) => s.setSelectedPlanFeature);
  const requestPlanFeatureFocus = useUIStore((s) => s.requestPlanFeatureFocus);
  const planMultiSelection = useUIStore((s) => s.planMultiSelection);
  const togglePlanMultiSelection = useUIStore((s) => s.togglePlanMultiSelection);
  const rangePlanMultiSelection = useUIStore((s) => s.rangePlanMultiSelection);
  const setPlanMultiSelection = useUIStore((s) => s.setPlanMultiSelection);
  const clearPlanMultiSelection = useUIStore((s) => s.clearPlanMultiSelection);
  const planBundleSelection = useUIStore((s) => s.planBundleSelection);
  const planBundleEditingId = useUIStore((s) => s.planBundleEditingId);
  const startPlanBundleSelection = useUIStore((s) => s.startPlanBundleSelection);
  const startPlanBundleEdit = useUIStore((s) => s.startPlanBundleEdit);
  const setPlanBundleSelection = useUIStore((s) => s.setPlanBundleSelection);
  const togglePlanBundleSelectionMember = useUIStore((s) => s.togglePlanBundleSelectionMember);
  const cancelPlanBundleSelection = useUIStore((s) => s.cancelPlanBundleSelection);
  const selectedPlanBundleId = useUIStore((s) => s.selectedPlanBundleId);
  const setSelectedPlanBundle = useUIStore((s) => s.setSelectedPlanBundle);
  const requestPlanBundleFocus = useUIStore((s) => s.requestPlanBundleFocus);

  const hiddenPartyIds = useDefensePlansStore((s) => s.hiddenPartyIds);
  // Filtered here, once: every consumer below (the parties section, the
  // assign picker via `parties`, feature/bundle rows via `who.parties` and
  // `who.links`) reads from these two, so a party mid-undo-toast
  // (DefensePlanPartyEditForm's deferred "Remove party") disappears from
  // all three at once without each of them learning about hiddenPartyIds.
  const allParties = ownPlan ? (partiesByPlan[ownPlan.id] ?? {}) : {};
  const allAssignments = ownPlan ? (assignmentsByPlan[ownPlan.id] ?? {}) : {};
  const anyHidden = Object.keys(hiddenPartyIds).length > 0;
  const parties: typeof allParties = anyHidden
    ? Object.fromEntries(Object.entries(allParties).filter(([id]) => !hiddenPartyIds[id]))
    : allParties;
  const assignments: typeof allAssignments = anyHidden
    ? Object.fromEntries(
        Object.entries(allAssignments).filter(([, a]) => !hiddenPartyIds[a.partyId])
      )
    : allAssignments;
  // Anonymous Fire Scouts can't write (ANON_WRITES_ENABLED) — the editor
  // itself is already gated behind a real account to get this far, but this
  // keeps the Assign action's visibility consistent with every other write
  // affordance in the app rather than assuming that.
  const canWriteAssignments = !anonWritesBlocked(user);
  const isDesktopShell = useDesktopShell();
  // Only one companion open at a time (panel-system.md's "never stack two
  // dialogs" — opening one closes another), so Assign, edit-party and plan
  // updates share one discriminated slot instead of three independent
  // booleans. The row that opened it keeps an active fill while it's open
  // (Sept 28 2026 review: the old notch on the companion's edge didn't read
  // in dark mode, and the hover fill left with the pointer).
  const [companion, setCompanion] = useState<
    | {
        kind: 'assign';
        target: { type: 'feature'; id: string } | { type: 'bundle'; id: string };
      }
    | { kind: 'party'; partyId: string }
    | { kind: 'updates' }
    | { kind: 'events'; bundleId: string }
    | { kind: 'bids'; bundleId: string }
    | { kind: 'vendors'; bundleId: string; highlightVendorIds?: string[] }
    | null
  >(null);
  const closeCompanion = () => setCompanion(null);
  const openCompanion = (next: NonNullable<typeof companion>) => {
    setCompanion(next);
    useUIStore.getState().setPanelNewest('left');
  };
  const assignTarget = companion?.kind === 'assign' ? companion.target : null;
  const editPartyId = companion?.kind === 'party' ? companion.partyId : null;
  const activeAssignFeatureId = assignTarget?.type === 'feature' ? assignTarget.id : null;
  const activeAssignBundleId = assignTarget?.type === 'bundle' ? assignTarget.id : null;
  const updatesOpen = companion?.kind === 'updates';
  const eventsBundleId = companion?.kind === 'events' ? companion.bundleId : null;
  const bidsBundleId = companion?.kind === 'bids' ? companion.bundleId : null;
  const vendorsBundleId = companion?.kind === 'vendors' ? companion.bundleId : null;
  const desktopCompanionFolded = useCompanionPlacement(PLAN_PANEL_WIDTH) === 'folded';
  // Keeps the companion mounted, with its last content, while it slides out.
  // Everything the desktop companion renders reads `shownCompanion` (and the
  // display* values derived from it below), not the live `companion`.
  const companionLeave = useLeavingPanel(companion != null, companion);
  const shownCompanion = companionLeave.lastContent ?? null;
  const displayAssignTarget = shownCompanion?.kind === 'assign' ? shownCompanion.target : null;
  // Browser history for whichever companion is open (panel-system.md's
  // "Deep links and back", Phase 4A) — desktop shell only, one entry
  // regardless of which of the three kinds is showing (they share the one
  // `companion` slot, so switching kinds without a close in between reuses
  // it rather than pushing a second entry).
  const closeCompanionAndPop = usePanelHistoryEntry(
    isDesktopShell,
    companion != null,
    'defense-plan-panel-companion',
    closeCompanion
  );
  // People, Layers and Program paint over this editor without unmounting it, which
  // left an open companion (Assign, edit party, updates) standing beside
  // them with its parent hidden. Close it the way its own close does.
  const browsePanelCovers = useUIStore(
    (s) => s.peoplePanelOpen || s.layersPanelOpen || s.programPanelOpen
  );
  useEffect(() => {
    if (browsePanelCovers && companion != null) closeCompanionAndPop();
    // Only a panel arriving should close it; closeCompanionAndPop is
    // re-created every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browsePanelCovers]);
  // `?plan=<id>&assign=<featureId>` deep link (usePanelDeepLinks.ts):
  // opens Assign for that feature once it's actually on this plan.
  const pendingPlanAssignFocus = useUIStore((s) => s.pendingPlanAssignFocus);
  useEffect(() => {
    if (!pendingPlanAssignFocus || !ownPlan) return;
    const feature = ownPlan.features.find((f) => f.id === pendingPlanAssignFocus.featureId);
    // An unknown feature id is just dropped.
    if (feature) openCompanion({ kind: 'assign', target: { type: 'feature', id: feature.id } });
    useUIStore.getState().clearPlanAssignFocus();
    // openCompanion is re-created every render — including it would re-run
    // this on every keystroke inside whatever's open. Only the pending
    // request and the plan it needs should re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPlanAssignFocus, ownPlan]);
  // A Program panel next move (uiStore.requestPlanCompanion): opens the
  // companion it names once that plan is the active one. Anything it names
  // that isn't on this plan any more is dropped, like the deep link above.
  const pendingPlanCompanion = useUIStore((s) => s.pendingPlanCompanion);
  useEffect(() => {
    if (!pendingPlanCompanion || !ownPlan) return;
    const { planId, companion: next } = pendingPlanCompanion;
    // A party can only be checked once this plan's parties have loaded.
    if (planId === ownPlan.id && next.kind === 'party' && !partiesByPlan[planId]) return;
    useUIStore.getState().clearPlanCompanion();
    if (planId !== ownPlan.id) return;
    const bundleIds = new Set(ownPlan.bundles.map((b) => b.id));
    const ok =
      next.kind === 'updates' ||
      (next.kind === 'party' && !!(partiesByPlan[ownPlan.id] ?? {})[next.partyId]) ||
      (next.kind === 'events' && bundleIds.has(next.bundleId)) ||
      (next.kind === 'bids' && bundleIds.has(next.bundleId)) ||
      (next.kind === 'vendors' && bundleIds.has(next.bundleId)) ||
      (next.kind === 'assign' &&
        (next.target.type === 'bundle'
          ? bundleIds.has(next.target.id)
          : ownPlan.features.some((f) => f.id === next.target.id)));
    if (ok) openCompanion(next);
    // Same reasoning as the assign deep link above: openCompanion is
    // re-created every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPlanCompanion, ownPlan, partiesByPlan]);
  const unpublishedUpdates = ownPlan
    ? unpublishedAssignmentUpdates(assignmentUpdatesByPlan[ownPlan.id] ?? {})
    : [];

  // Bundle picker (the "Bundle" TextLink below): the optional name the
  // owner typed, and the name a blank field falls back to.
  const [bundleNameDraft, setBundleNameDraft] = useState('');
  // The selection bar's "Add to…" menu.
  const [selectionAddOpen, setSelectionAddOpen] = useState(false);
  const selectionAddRef = useRef<View>(null);
  const bundleDefaultName = useMemo(
    () =>
      planBundleSelection
        ? defaultBundleName(
            planBundleSelection,
            ownPlan?.features ?? [],
            (ownPlan?.bundles ?? []).map((b) => b.name),
            (strategy) => DEFENSE_STRATEGIES[strategy as DefenseStrategy].label
          )
        : '',
    [planBundleSelection, ownPlan?.features, ownPlan?.bundles]
  );

  // Collapse state per bundle, keyed by id, only for bundles the user has
  // explicitly toggled — everything else falls back to the default (open
  // at BUNDLE_COLLAPSE_THRESHOLD members or fewer, closed above it, see
  // resolveBundleExpanded below).
  const [bundleExpandedOverrides, setBundleExpandedOverrides] = useState<Record<string, boolean>>(
    {}
  );
  const resolveBundleExpanded = (bundleId: string, memberCount: number): boolean =>
    bundleExpandedOverrides[bundleId] ?? memberCount <= BUNDLE_COLLAPSE_THRESHOLD;
  const toggleBundleExpanded = (bundleId: string, memberCount: number) => {
    setBundleExpandedOverrides((prev) => ({
      ...prev,
      [bundleId]: !resolveBundleExpanded(bundleId, memberCount),
    }));
  };
  // Shape click on the map: open the feature's bundle, scroll its row into
  // view.
  useRevealSelectedPlanRow(
    selectedPlanFeatureId,
    ownPlan?.features,
    resolveBundleExpanded,
    (bundleId) => setBundleExpandedOverrides((prev) => ({ ...prev, [bundleId]: true }))
  );

  // A defense drawn just now: its row slides in and takes the selection,
  // which also scrolls it into view (useRevealSelectedPlanRow above) and
  // lights its shape on the map. No camera move: the user drew it there.
  const justDrawnIds = useJustDrawnFeatureIds(ownPlan?.features, setSelectedPlanFeature);

  // What the picker lists: loose defenses, plus the edited bundle's own
  // members when editing. A defense sits in at most one bundle, and one in
  // another bundle is moved from that bundle's menu, not picked here.
  const pickableFeatures = useMemo(
    () =>
      (ownPlan?.features ?? []).filter(
        (f) => !f.bundleId || (planBundleEditingId !== null && f.bundleId === planBundleEditingId)
      ),
    [ownPlan?.features, planBundleEditingId]
  );
  const looseFeatureCount = (ownPlan?.features ?? []).filter((f) => !f.bundleId).length;
  const editingBundle = planBundleEditingId
    ? ownPlan?.bundles?.find((b) => b.id === planBundleEditingId)
    : undefined;
  // A map click can tick a shape the list hides (another bundle's member),
  // so saves keep only what the list offers.
  const pickedIds = (planBundleSelection ?? []).filter((id) =>
    pickableFeatures.some((f) => f.id === id)
  );

  // Select all / Clear in the picker's label row, and Cmd/Ctrl+A. Ticks stay
  // in the order they were made; new ones follow in list order.
  const allPicked = pickableFeatures.length > 0 && pickedIds.length === pickableFeatures.length;
  const handleSelectAllPickable = () => {
    const ordered = orderFeaturesNewestFirst(pickableFeatures).map((f) => f.id);
    setPlanBundleSelection([...pickedIds, ...ordered.filter((id) => !pickedIds.includes(id))]);
  };
  const handleClearPicked = () => setPlanBundleSelection([]);

  const handleBundleStart = () => {
    setBundleNameDraft('');
    startPlanBundleSelection();
  };
  const handleBundleCancel = () => {
    cancelPlanBundleSelection();
    setBundleNameDraft('');
  };
  const handleBundleEdit = (bundleId: string) => {
    setBundleNameDraft('');
    startPlanBundleEdit(
      bundleId,
      (ownPlan?.features ?? []).filter((f) => f.bundleId === bundleId).map((f) => f.id)
    );
  };
  const handleBundleSave = () => {
    if (!planBundleSelection || pickedIds.length === 0) return;
    // Emptying a bundle is Unbundle's job, so Save stays off at zero. Who a
    // defense is assigned to is per defense and doesn't change here.
    if (planBundleEditingId) {
      void setBundleMembers(planBundleEditingId, pickedIds);
      useUIStore.getState().showToast({ message: 'Bundle updated' });
      cancelPlanBundleSelection();
      return;
    }
    const name = bundleNameDraft.trim() || bundleDefaultName;
    if (!name) return;
    void createBundle(name, pickedIds);
    useUIStore.getState().showToast({ message: 'Bundle saved' });
    cancelPlanBundleSelection();
    setBundleNameDraft('');
  };
  const handleBundleHeaderSelect = (bundleId: string) => {
    setSelectedPlanBundle(bundleId);
    requestPlanBundleFocus(bundleId);
  };

  // Before the first defense there is no plan doc, so the title shown is the
  // one a new plan will get, or one the owner has typed (the store holds it
  // as pendingTitle until the first draw creates the doc).
  const pendingTitle = useDefensePlansStore((s) => s.pendingTitle);
  const unsavedTitle =
    pendingTitle ?? (user ? defaultNewPlanTitle(ownPlans.length, user) : 'My plan');
  const shownTitle = ownPlan?.title ?? unsavedTitle;
  const [titleDraft, setTitleDraft] = useState(shownTitle);
  useEffect(() => {
    setTitleDraft(shownTitle);
  }, [ownPlan?.id, shownTitle]);

  // "Add a defense" region: the tile grid starts expanded every time the
  // panel opens (or switches to a different plan) — an audit or a
  // first-time user has to reach any tile in one click, with nothing to
  // expand first. Picking a strategy (or clearing one) collapses it; the
  // "Change strategy" link and the "Add a defense" button both re-expand it.
  // An empty plan ignores this flag entirely and always shows the grid — see
  // isEmptyPlan/showExpandedGrid below.
  const [paletteExpanded, setPaletteExpanded] = useState(true);
  useEffect(() => {
    setPaletteExpanded(true);
  }, [ownPlan?.id]);

  // Back to the strategy grid once a line or area commits (Glenn's Sept 23
  // call, item 8 — "could not get back to the strategy icons"): a committed
  // feature grows ownPlan.features, so re-expand the palette when that
  // happens while a line/area tool is armed. Points and homes stay
  // collapsed on purpose — Glenn drops several of those in a row and
  // re-expanding after each one would fight that. The tool itself stays
  // armed either way, same as today (useDefensePlanDraw.web.ts commits and
  // leaves the strategy/kind selected for the next shape).
  //
  // Guarded against firing on a plan switch: switching plans changes
  // ownPlan.features.length too (a different plan has a different feature
  // count), which would otherwise read as "a feature was just added" and
  // pop the grid open on every switch. The ref tracks the previous length
  // per plan id, not just the previous length, so a switch updates the ref
  // without re-expanding.
  const prevFeatureCountRef = useRef<{ planId: string | null; count: number }>({
    planId: ownPlan?.id ?? null,
    count: ownPlan?.features.length ?? 0,
  });
  useEffect(() => {
    const planId = ownPlan?.id ?? null;
    const count = ownPlan?.features.length ?? 0;
    const prev = prevFeatureCountRef.current;
    const samePlan = prev.planId === planId;
    prevFeatureCountRef.current = { planId, count };
    if (!samePlan) return;
    if (count > prev.count && (planDrawTool?.kind === 'line' || planDrawTool?.kind === 'area')) {
      setPaletteExpanded(true);
    }
  }, [ownPlan?.id, ownPlan?.features.length, planDrawTool?.kind]);

  const [titleFocused, setTitleFocused] = useState(false);
  const { hovered: titleHovered, hoverProps: titleHoverProps } = useWebHover();

  const [justCopied, setJustCopied] = useState(false);
  useEffect(() => {
    if (!justCopied) return;
    const timer = setTimeout(() => setJustCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [justCopied]);

  const propertyPinsVisible = useUIStore((s) => s.propertyPinsVisible);
  const planHomeMissId = useUIStore((s) => s.planHomeMissId);
  // Transient "no home there" flash, same debounce shape as `justCopied`
  // above: bumped by a ref-counter in the store (planHomeMissId) rather than
  // a boolean so a second miss in a row still restarts the timer.
  const [showHomeMiss, setShowHomeMiss] = useState(false);
  const homeMissMountRef = useRef(true);
  useEffect(() => {
    // Skip the mount render — planHomeMissId starts at 0 for every session,
    // not "a miss just happened".
    if (homeMissMountRef.current) {
      homeMissMountRef.current = false;
      return;
    }
    setShowHomeMiss(true);
    const timer = setTimeout(() => setShowHomeMiss(false), HOME_MISS_HINT_MS);
    return () => clearTimeout(timer);
  }, [planHomeMissId]);

  // "Draw a little longer" flash after a finish gesture on a stroke with too
  // few points; same counter-and-timer shape as the home miss above.
  const planTooShortId = useUIStore((s) => s.planTooShortId);
  const [showTooShort, setShowTooShort] = useState(false);
  const tooShortMountRef = useRef(true);
  useEffect(() => {
    if (tooShortMountRef.current) {
      tooShortMountRef.current = false;
      return;
    }
    setShowTooShort(true);
    const timer = setTimeout(() => setShowTooShort(false), TOO_SHORT_HINT_MS);
    return () => clearTimeout(timer);
  }, [planTooShortId]);

  // Plan switcher menu (title + chevron, next to the editable title).
  // Closing on Escape is handled through the shared escapeStack, the same
  // way ConfirmDialog does, so a press closes only the topmost layer — the
  // menu first, never the whole panel underneath it in the same press.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  useEscapeToClose(switcherOpen, () => setSwitcherOpen(false));
  // Plan actions (the ⋯ beside close): Duplicate and Delete, split out of
  // the switcher on Sept 28 so the chevron only switches plans, as in the
  // panel-system prototype. Opening one menu closes the other.
  const [actionsOpen, setActionsOpen] = useState(false);
  useEscapeToClose(actionsOpen, () => setActionsOpen(false));
  useEffect(() => {
    setSwitcherOpen(false);
    setActionsOpen(false);
  }, [ownPlan?.id]);
  const anyMenuOpen = switcherOpen || actionsOpen;

  const handleSwitchPlan = (planId: string) => {
    setSwitcherOpen(false);
    if (planId === ownPlan?.id) return;
    setActiveOwnPlan(planId);
  };

  const handleNewPlan = () => {
    setSwitcherOpen(false);
    startNewPlan();
  };

  // Opening the editor leaves the camera alone (Map.web.tsx), so this is how
  // the owner asks for the whole-plan fit.
  const requestPlanFit = useUIStore((s) => s.requestPlanFit);
  const handleZoomToPlan = () => {
    setActionsOpen(false);
    requestPlanFit();
  };

  const handleDuplicatePlan = () => {
    setActionsOpen(false);
    void duplicateActivePlan();
  };

  const handleDeletePlan = async () => {
    setActionsOpen(false);
    const plan = ownPlan;
    if (!plan) return;
    const confirmed = await confirm({
      title: `Delete "${plan.title}"?`,
      message:
        "This removes the plan and its defenses for everyone with the link. It can't be undone.",
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!confirmed) return;
    void deleteOwnPlan(plan.id);
  };

  // One press deletes one defense, and the next one has to be aimed. Deleting
  // a row collapses the list, so the row below slides up under a cursor that
  // never moved and the next press lands on a different feature's delete
  // button. That is how a 20-defense plan emptied itself in dev on Sept 22
  // 2026 while its owner thought he was deleting the plan. Re-arming on the
  // next pointer move or key press costs a real second delete nothing (it
  // needs the aim anyway) and leaves the layout alone.
  const deleteArmedRef = useRef(true);
  const detachRearmRef = useRef<(() => void) | null>(null);

  const disarmDeleteUntilInput = () => {
    // No document under the react-native test environment; nothing to guard
    // there either, since a press can only chain through a real pointer.
    if (typeof document === 'undefined') return;
    detachRearmRef.current?.();
    deleteArmedRef.current = false;
    const rearm = () => {
      deleteArmedRef.current = true;
      detach();
    };
    const detach = () => {
      document.removeEventListener('mousemove', rearm);
      document.removeEventListener('keydown', rearm);
      detachRearmRef.current = null;
    };
    detachRearmRef.current = detach;
    document.addEventListener('mousemove', rearm);
    document.addEventListener('keydown', rearm);
  };

  useEffect(() => {
    return () => detachRearmRef.current?.();
  }, []);

  // False while this panel slides out: the Escape listener below detaches.
  const panelShown = usePanelShown();

  // Escape closes the panel, but only when no draw tool is armed (the draw
  // hook's own document keydown listener owns Escape while a tool is active
  // — see useDefensePlanDraw.web.ts) and focus isn't in a text field (so
  // Escape while editing the title doesn't blow away the whole panel).
  useEffect(() => {
    // Guards a jest render (react-native test environment, no DOM) rather
    // than a real runtime condition — this component only ever mounts on
    // web, where `document` always exists.
    if (typeof document === 'undefined' || !panelShown) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A dialog over the panel owns this press (utils/escapeStack).
      if (isEscapeLayerOpen()) return;
      const ui = useUIStore.getState();
      if (ui.planDrawTool) return;
      // Cancels the bundle picker first, ahead of the typing guard below —
      // Escape while the sticky bar's name field is focused should still
      // back out of select mode, not be swallowed as "don't close while
      // typing".
      if (ui.planBundleSelection) {
        ui.cancelPlanBundleSelection();
        return;
      }
      // A multi-selection clears first; the next Escape closes the panel.
      if (ui.planMultiSelection.length > 0) {
        ui.clearPlanMultiSelection();
        return;
      }
      const el = document.activeElement as HTMLElement | null;
      const isTyping =
        !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (isTyping) return;
      closePlanPanel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closePlanPanel, panelShown]);

  const commitTitle = () => {
    const next = titleDraft.trim();
    if (!next || next === shownTitle) return;
    void updateTitle(next);
  };

  const handleStrategyPress = (strategy: DefenseStrategy) => {
    // Either picking or clearing a strategy collapses the grid — see the
    // paletteExpanded doc comment above. A cleared strategy on a non-empty
    // plan then reads as the "Add a defense" button; an empty plan ignores
    // this and keeps the grid open regardless.
    setPaletteExpanded(false);
    if (planDrawTool?.strategy === strategy) {
      setPlanDrawTool(null);
      return;
    }
    // Each strategy arms the shape it usually takes (a water tank is a point,
    // brush clearing a line); "Draw as" still changes it for this defense.
    setPlanDrawTool({ strategy, kind: DEFENSE_STRATEGIES[strategy].defaultKind });
  };

  const handleKindPress = (kind: PlanDrawKind) => {
    if (planDrawTool && planDrawTool.kind !== kind) {
      setPlanDrawTool({ strategy: planDrawTool.strategy, kind });
    }
  };

  // The pill flips at once (the store is optimistic), so the toast says so
  // at once too. If the write fails the store rolls the plan back and this
  // error toast replaces the confirmation.
  const handleVisibilityChange = async (v: PlanVisibility) => {
    useUIStore
      .getState()
      .showToast({ message: v === 'shared' ? 'Plan shared' : 'Plan is private' });
    const saved = await setVisibility(v);
    if (!saved) {
      useUIStore.getState().showToast({ message: "Couldn't change who can see this plan" });
    }
  };

  // Fade the footer's Copy link / Private note swap, but not its first paint.
  const footerVisibility = ownPlan?.visibility;
  const footerVisibilityRef = useRef(footerVisibility);
  const [footerSwapped, setFooterSwapped] = useState(false);
  useEffect(() => {
    if (footerVisibilityRef.current === footerVisibility) return;
    footerVisibilityRef.current = footerVisibility;
    setFooterSwapped(true);
  }, [footerVisibility]);

  const handleShare = async () => {
    if (!ownPlan) return;
    const result = await sharePlan({ planId: ownPlan.id, title: ownPlan.title });
    if (result === 'copied') {
      useUIStore.getState().showToast({ message: 'Link copied' });
      setJustCopied(true);
    }
    if (result === 'failed') {
      useUIStore.getState().showToast({ message: "Couldn't copy the link" });
    }
  };

  // The needs list for a vendor call: every defense, its size, where it is,
  // who has it and what they said they would do, including the ones nobody
  // has taken. Built in the browser; nothing server side is involved.
  const handleDownloadNeeds = () => {
    if (!ownPlan) return;
    const csv = buildPlanNeedsCsv({
      plan: ownPlan,
      parties: Object.values(parties),
      assignments: Object.values(assignments),
    });
    downloadText(csv, planNeedsFilename(ownPlan), 'text/csv');
  };

  const handleFeatureSelect = (featureId: string) => {
    setSelectedPlanFeature(featureId);
    requestPlanFeatureFocus(featureId);
  };

  // Delete is immediate and optimistic. The toast's Undo (utils/undoGroup)
  // adds the feature back under the same id; deletes inside one toast window
  // share a single Undo. Returns false when the re-arm guard swallowed the
  // press, so the row can pulse its trash icon.
  const handleDelete = (feature: DefensePlanFeature): boolean => {
    if (!deleteArmedRef.current) return false;
    disarmDeleteUntilInput();
    const links = linksForFeature(feature.id, assignments);
    const planId = ownPlan?.id;
    void removeFeature(feature.id);
    const label = DEFENSE_STRATEGIES[feature.strategy].label;
    addUndo({
      message: `Deleted ${label}`,
      verb: 'Deleted',
      noun: 'defenses',
      restore: async () => {
        const store = useDefensePlansStore.getState();
        const plan = store.ownPlan;
        // The user switched plans while the toast was up: nothing to put
        // the feature back into.
        if (!plan || plan.id !== planId) return;
        // Only carries the bundle back if it's still there.
        const bundleStillExists =
          !!feature.bundleId && plan.bundles.some((b) => b.id === feature.bundleId);
        // Same id, so a restored owner-added party lands right back on it
        // (see the store's addFeature doc comment). Only source:'owner'
        // links can be restored; a volunteer's own link needs their uid to
        // recreate (the rules check it via get()).
        await store.addFeature({
          id: feature.id,
          strategy: feature.strategy,
          kind: feature.kind,
          coords: feature.coords,
          structureId: feature.structureId,
          address: feature.address,
          bundleId: bundleStillExists ? feature.bundleId : undefined,
        });
        for (const link of links) {
          if (link.source !== 'owner') continue;
          void store.setOwnerAssignment(plan.id, feature.id, link.partyId, {
            description: link.description,
            status: link.status,
          });
        }
      },
    });
    return true;
  };

  // "Copy as another defense": adds the copy, selects it, and offers Undo
  // (which just removes it). Selecting does not fit the map: the shape is
  // already in view, since it sits where the source does.
  const handleCopyAs = (source: DefensePlanFeature, strategy: DefenseStrategy) => {
    const planId = ownPlan?.id;
    const newId = useDefensePlansStore.getState().copyFeatureAs(source.id, strategy);
    if (!newId) return;
    const ui = useUIStore.getState();
    ui.setSelectedPlanFeature(newId);
    ui.setLastPlanCopy({ sourceId: source.id, copyId: newId });
    addUndo({
      message: `Copied as ${DEFENSE_STRATEGIES[strategy].label}`,
      verb: 'Copied',
      noun: 'defenses',
      restore: async () => {
        const store = useDefensePlansStore.getState();
        // The user switched plans while the toast was up.
        if (store.ownPlan?.id !== planId) return;
        await store.removeFeature(newId);
        const after = useUIStore.getState();
        if (after.selectedPlanFeatureId === newId) after.setSelectedPlanFeature(null);
        if (after.lastPlanCopy?.copyId === newId) after.setLastPlanCopy(null);
      },
    });
  };

  // Add, move or remove defenses from a bundle (bundleId null takes them out
  // of theirs). Used by each row's ⋯ menu; drag and a selection bar call it
  // too. Offers Undo, which puts every moved defense back in one write.
  const handleMoveToBundle = async (featureIds: string[], bundleId: string | null) => {
    const plan = ownPlan;
    if (!plan || featureIds.length === 0) return;
    const planId = plan.id;
    const moving = plan.features.filter((f) => featureIds.includes(f.id));
    const bundleName = (id: string | undefined) => plan.bundles.find((b) => b.id === id)?.name;
    const subject =
      featureIds.length === 1
        ? (featureLabels[featureIds[0]] ?? 'defense')
        : `${featureIds.length} defenses`;
    const snapshot = await moveFeaturesToBundle(featureIds, bundleId);
    if (!snapshot) return;
    let message: string;
    let verb: string;
    if (bundleId === null) {
      message = `Took ${subject} out of ${bundleName(moving[0]?.bundleId) ?? 'its bundle'}`;
      verb = 'Took out';
    } else if (moving.some((f) => f.bundleId)) {
      message = `Moved ${subject} to ${bundleName(bundleId) ?? 'the bundle'}`;
      verb = 'Moved';
    } else {
      message = `Added ${subject} to ${bundleName(bundleId) ?? 'the bundle'}`;
      verb = 'Added';
    }
    if (bundleId !== null) {
      setBundleExpandedOverrides((prev) => ({ ...prev, [bundleId]: true }));
    }
    addUndo({
      message,
      verb,
      noun: 'defenses',
      restore: async () => {
        if (useDefensePlansStore.getState().ownPlan?.id !== planId) return;
        await restoreBundleMembership(snapshot);
      },
    });
  };

  // Delete every defense in a selection. One Undo puts them all back, each
  // with its owner-added links, as handleDelete does for one.
  const handleDeleteMany = (ids: string[]): boolean => {
    if (!ownPlan) return false;
    const features = ownPlan.features.filter((f) => ids.includes(f.id));
    if (features.length === 0) return false;
    if (!deleteArmedRef.current) return false;
    disarmDeleteUntilInput();
    const planId = ownPlan.id;
    const linksById = new Map(features.map((f) => [f.id, linksForFeature(f.id, assignments)]));
    for (const feature of features) void removeFeature(feature.id);
    clearPlanMultiSelection();
    const noun = features.length === 1 ? 'defense' : 'defenses';
    addUndo({
      message: `Deleted ${features.length} ${noun}`,
      verb: 'Deleted',
      noun: 'defenses',
      restore: async () => {
        const store = useDefensePlansStore.getState();
        const plan = store.ownPlan;
        if (!plan || plan.id !== planId) return;
        for (const feature of features) {
          const bundleStillExists =
            !!feature.bundleId && plan.bundles.some((b) => b.id === feature.bundleId);
          await store.addFeature({
            id: feature.id,
            strategy: feature.strategy,
            kind: feature.kind,
            coords: feature.coords,
            structureId: feature.structureId,
            address: feature.address,
            bundleId: bundleStillExists ? feature.bundleId : undefined,
          });
          for (const link of linksById.get(feature.id) ?? []) {
            if (link.source !== 'owner') continue;
            void store.setOwnerAssignment(plan.id, feature.id, link.partyId, {
              description: link.description,
              status: link.status,
            });
          }
        }
      },
    });
    return true;
  };

  // The ids still in the plan, in pick order. The bar and the row menus read
  // this, so a deleted or undone defense never leaves a ghost in the count.
  const planFeatureIds = new Set((ownPlan?.features ?? []).map((f) => f.id));
  const selectionIds = planMultiSelection.filter((id) => planFeatureIds.has(id));
  const hasSelection = selectionIds.length >= 2;
  const selectionAnyBundled = (ownPlan?.features ?? []).some(
    (f) => selectionIds.includes(f.id) && !!f.bundleId
  );
  // Every pick already sits in one bundle: nothing for "Bundle" to do.
  const selectionSharesBundle = (() => {
    if (!ownPlan || selectionIds.length === 0) return false;
    const picked = ownPlan.features.filter((f) => selectionIds.includes(f.id));
    const first = picked[0]?.bundleId;
    return !!first && picked.every((f) => f.bundleId === first);
  })();

  // "Bundle" on a selection: makes the bundle with its default name and opens
  // the name for editing on the new header. Undo unbundles it.
  const handleBundleSelection = async (ids: string[], opts?: { keepSelection?: boolean }) => {
    if (!ownPlan || ids.length < 2) return;
    const planId = ownPlan.id;
    const name = defaultBundleName(
      ids,
      ownPlan.features,
      ownPlan.bundles.map((b) => b.name),
      (strategy) => DEFENSE_STRATEGIES[strategy as DefenseStrategy].label
    );
    if (!name) return;
    const newId = await createBundle(name, ids);
    if (!newId) return;
    if (!opts?.keepSelection) clearPlanMultiSelection();
    setBundleExpandedOverrides((prev) => ({ ...prev, [newId]: true }));
    useUIStore.getState().setPlanBundleRenamingId(newId);
    addUndo({
      message: `Bundled ${ids.length} defenses`,
      verb: 'Bundled',
      noun: 'defenses',
      restore: async () => {
        if (useDefensePlansStore.getState().ownPlan?.id !== planId) return;
        await useDefensePlansStore.getState().ungroupBundle(newId);
      },
    });
  };

  // The ⋯ menu every editable row carries (DefensePlanFeatureRow `menu`).
  // A row inside a selection of 2 or more acts on the whole selection.
  const rowMenuFor = (feature: DefensePlanFeature) => {
    const bundles = (ownPlan?.bundles ?? []).map(({ id, name }) => ({ id, name }));
    if (hasSelection && selectionIds.includes(feature.id)) {
      const ids = [...selectionIds];
      return {
        bundles,
        onAddToBundle: (bundleId: string) => {
          void handleMoveToBundle(ids, bundleId);
          clearPlanMultiSelection();
        },
        onNewBundle: () => void handleBundleSelection(ids),
        onRemoveFromBundle: selectionAnyBundled
          ? () => {
              void handleMoveToBundle(ids, null);
              clearPlanMultiSelection();
            }
          : undefined,
        selection: {
          count: ids.length,
          anyBundled: selectionAnyBundled,
          onDelete: () => void handleDeleteMany(ids),
        },
      };
    }
    return {
      bundles,
      onAddToBundle: (bundleId: string) => void handleMoveToBundle([feature.id], bundleId),
      onNewBundle: () => {
        setBundleNameDraft('');
        startPlanBundleSelection([feature.id]);
      },
      onRemoveFromBundle: feature.bundleId
        ? () => void handleMoveToBundle([feature.id], null)
        : undefined,
      onOpenForBids: feature.bundleId ? undefined : () => void handleOpenItemForBids(feature.id),
    };
  };

  // Bids live on bundles, so a loose defense opened for bids becomes a
  // bundle of one, named the way "Bundle" names it. Undo closes the bids
  // and unbundles it again.
  const handleOpenItemForBids = async (featureId: string) => {
    if (!ownPlan) return;
    const planId = ownPlan.id;
    const name = defaultBundleName(
      [featureId],
      ownPlan.features,
      ownPlan.bundles.map((b) => b.name),
      (strategy) => DEFENSE_STRATEGIES[strategy as DefenseStrategy].label
    );
    if (!name) return;
    const store = useDefensePlansStore.getState();
    const newId = await createBundle(name, [featureId]);
    const opened = newId ? await store.setBundleBidsOpen(planId, newId, true) : false;
    const { showToast } = useUIStore.getState();
    if (!newId || !opened) {
      showToast({ message: "Couldn't open it for bids" });
      return;
    }
    setBundleExpandedOverrides((prev) => ({ ...prev, [newId]: true }));
    openCompanion({ kind: 'bids', bundleId: newId });
    showToast({
      message: `Open for bids as ${name}`,
      action: {
        label: 'Undo',
        onPress: () => {
          void (async () => {
            const s = useDefensePlansStore.getState();
            if (s.ownPlan?.id !== planId) return;
            setCompanion((c) => (c?.kind === 'bids' && c.bundleId === newId ? null : c));
            await s.setBundleBidsOpen(planId, newId, false);
            await s.ungroupBundle(newId);
          })();
        },
      },
    });
  };

  // Vendor bids (Oct 2026). Marking work done lets a moderator release payment, so it gets an Undo
  // toast rather than a confirm (panel-system rule: reversible = undo).
  const handleToggleWorkDone = async (bundleId: string) => {
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const planId = ownPlan.id;
    const done = !bundle.workDoneAt;
    const { setBundleWorkDone } = useDefensePlansStore.getState();
    const ok = await setBundleWorkDone(planId, bundleId, done);
    const { showToast } = useUIStore.getState();
    if (!ok) {
      showToast({ message: "Couldn't update the work" });
      return;
    }
    showToast({
      message: done ? `${bundle.name} marked done` : 'Work reopened',
      action: {
        label: 'Undo',
        onPress: () => {
          void setBundleWorkDone(planId, bundleId, !done).then((undone) => {
            if (!undone) showToast({ message: "Couldn't undo" });
          });
        },
      },
    });
  };

  const handleToggleBids = async (bundleId: string) => {
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const open = !bundle.bidsOpen;
    const ok = await setBundleBidsOpen(ownPlan.id, bundleId, open);
    if (!ok) {
      useUIStore.getState().showToast({ message: "Couldn't change the bids setting" });
      return;
    }
    if (open) openCompanion({ kind: 'bids', bundleId });
    else useUIStore.getState().showToast({ message: `Bids closed on ${bundle.name}` });
  };

  const handleCopyOpportunityLink = async (bundleId: string) => {
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    const result = await shareBundleOpportunity({
      planId: ownPlan.id,
      bundleId,
      bundleName: bundle.name,
    });
    if (result === 'copied') useUIStore.getState().showToast({ message: 'Link copied' });
    if (result === 'failed') {
      useUIStore.getState().showToast({ message: "Couldn't copy the link" });
    }
  };

  const handleUngroup = async (bundleId: string) => {
    if (!ownPlan) return;
    const bundle = ownPlan.bundles.find((b) => b.id === bundleId);
    if (!bundle) return;
    // A bundle with an upcoming event: people may have signed up, so ask
    // first, then cancel those events (the public page says so) before the
    // bundle goes. Undo brings the bundle back but not the events.
    // Vendors' bids hang off the bundle id too, so without the bundle they
    // have nowhere to show and the opportunity link stops working; Undo
    // brings the bundle, and so the bids, back. One question covers both.
    const upcoming = upcomingEventsForBundle(planEvents ?? [], ownPlan.id, bundleId);
    const bidCount = (planBids ?? []).filter((b) => b.bundleId === bundleId).length;
    const warnings: string[] = [];
    if (upcoming.length > 0) {
      warnings.push(
        `This bundle has ${upcoming.length} upcoming ${upcoming.length === 1 ? 'event' : 'events'}. Unbundling cancels ${upcoming.length === 1 ? 'it' : 'them'}.`
      );
    }
    if (
      (planFundingRequests ?? []).some((r) => r.bundleId === bundleId && isLiveFundingRequest(r))
    ) {
      warnings.push('This bundle has funding attached.');
    }
    if (bidCount > 0) {
      warnings.push(
        `Vendors have sent ${bidCount} ${bidCount === 1 ? 'bid' : 'bids'} on it. Unbundling hides them and stops the opportunity link from working.`
      );
    } else if (bundle.bidsOpen) {
      warnings.push('It is open for bids. Unbundling stops the opportunity link from working.');
    }
    if (warnings.length > 0) {
      const ok = await confirm({
        title: `Unbundle ${bundle.name}?`,
        message: warnings.join(' '),
        confirmLabel: 'Unbundle',
        destructive: true,
      });
      if (!ok) return;
      if (upcoming.length > 0) await cancelUpcomingForBundle(ownPlan.id, bundleId);
      if (
        (companion?.kind === 'events' ||
          companion?.kind === 'bids' ||
          companion?.kind === 'vendors') &&
        companion.bundleId === bundleId
      ) {
        closeCompanion();
      }
    }
    const memberIds = ownPlan.features.filter((f) => f.bundleId === bundleId).map((f) => f.id);
    const planId = ownPlan.id;
    void ungroupBundle(bundleId);
    addUndo({
      message: `Unbundled ${bundle.name}`,
      verb: 'Unbundled',
      noun: 'bundles',
      restore: async () => {
        if (useDefensePlansStore.getState().ownPlan?.id !== planId) return;
        await useDefensePlansStore.getState().restoreBundle(bundle, memberIds);
      },
    });
  };

  // The rows on screen, top to bottom, for the keyboard (usePlanShortcuts)
  // and the roving tab stop: exactly one row is a Tab target, the selected
  // one, or the first when nothing on screen is selected.
  const keptBundleIds = useMemo(() => {
    if (!ownPlan) return undefined;
    const keep = bundleIdsWithUpcomingEvents(planEvents ?? [], ownPlan.id);
    for (const id of bundleIdsKeptByBids(ownPlan.bundles ?? [], planBids ?? [])) keep.add(id);
    for (const id of bundleIdsKeptByFunding(planFundingRequests ?? [])) keep.add(id);
    return keep;
  }, [ownPlan, planEvents, planBids, planFundingRequests]);
  const listEntries = groupFeaturesForList(
    ownPlan?.features ?? [],
    ownPlan?.bundles ?? [],
    keptBundleIds
  );
  const visibleIds = visibleFeatureIds(listEntries, resolveBundleExpanded);
  const rovingRowId =
    selectedPlanFeatureId && visibleIds.includes(selectedPlanFeatureId)
      ? selectedPlanFeatureId
      : (visibleIds[0] ?? null);
  const shortcutsLive = planShortcutsLive({
    isDesktopShell,
    panelShown,
    hasOwnPlan: !!ownPlan,
    bundleSelecting: !!planBundleSelection,
  });
  // "A" opens Assign only where the handler is wired (canWriteAssignments),
  // so the rows advertise it only then.
  const assignKeyLive = shortcutsLive && canWriteAssignments;
  // Row click: Cmd/Ctrl toggles it in the selection, Shift picks the run from
  // the anchor, a plain click is the single select and map fit as before.
  const bundleButton = (
    <Button
      title="Bundle"
      onPress={() => void handleBundleSelection([...selectionIds])}
      variant="secondary"
      size="sm"
      disabled={selectionSharesBundle}
      testID="plan-selection-bundle"
      style={styles.selectionAction}
    />
  );
  const handleRowPress = (featureId: string, e?: unknown) => {
    if (isToggleClick(e)) {
      togglePlanMultiSelection(featureId);
    } else if (isRangeClick(e)) {
      rangePlanMultiSelection(featureId, visibleIds);
    } else {
      handleFeatureSelect(featureId);
    }
  };
  usePlanShortcuts(
    {
      strategies: DEFENSE_STRATEGY_ORDER,
      onPickStrategy: (strategy) => handleStrategyPress(strategy as DefenseStrategy),
      visibleFeatureIds: visibleIds,
      onSelectFeature: handleFeatureSelect,
      onDeleteFeature: (id) => {
        const feature = ownPlan?.features.find((f) => f.id === id);
        if (feature) handleDelete(feature);
      },
      onDeleteSelection: (ids) => handleDeleteMany(ids),
      onSelectAllRows: () => setPlanMultiSelection(visibleIds),
      onAssignFeature: canWriteAssignments
        ? (id) => openCompanion({ kind: 'assign', target: { type: 'feature', id } })
        : undefined,
      onCopyFeature: (id) => {
        if (ownPlan?.features.some((f) => f.id === id)) {
          useUIStore.getState().setPlanCopyMenuFeature(id);
        }
      },
      onOpenRowMenu: (id) => {
        if (ownPlan?.features.some((f) => f.id === id)) {
          useUIStore.getState().setPlanRowMenuFeature(id);
        }
      },
    },
    shortcutsLive,
    { pickerOpen: !!planBundleSelection, onSelectAllPickable: handleSelectAllPickable }
  );

  // Drag a row onto a bundle card, out of one, or onto another loose row
  // (usePlanListDrag.web.ts). The ⋯ menu stays the keyboard path. The
  // selection is left as it was after a drop.
  const listDragRef = useRef<View>(null);
  const middleScrollRef = useRef<ScrollView>(null);
  const dragGhostRef = useRef<View>(null);
  const dropHighlight = useUIStore((s) => dropHighlightKey(s.planListDrag));
  // A member is being dragged (the strip at the list's end shows), and the
  // pointer is over a spot that would take it out (the strip lights).
  const dragFromBundle = useUIStore((s) => !!s.planListDrag?.sourceBundleId);
  const looseTargeted = useUIStore(
    (s) =>
      s.planListDrag?.target?.type === 'loose' &&
      s.planListDrag.outcome?.kind === 'remove' &&
      !s.planListDrag.outcome.disabled
  );
  usePlanListDrag({
    listRef: listDragRef,
    scrollRef: middleScrollRef,
    ghostRef: dragGhostRef,
    enabled: !!ownPlan && ownPlan.features.length > 0,
    resolve: (drag, target) =>
      ownPlan ? resolvePlanDrop(drag, target, ownPlan, featureLabels, MAX_BUNDLES_PER_PLAN) : null,
    onDrop: (outcome, ids) => {
      if (!ownPlan) return;
      if (outcome.kind === 'create') {
        if (outcome.withFeatureId) {
          void handleBundleSelection([...ids, outcome.withFeatureId], { keepSelection: true });
        }
        return;
      }
      const bundleOf = (id: string) => ownPlan.features.find((f) => f.id === id)?.bundleId;
      if (outcome.kind === 'remove') {
        void handleMoveToBundle(
          ids.filter((id) => !!bundleOf(id)),
          null
        );
      } else if (outcome.bundleId) {
        const target = outcome.bundleId;
        void handleMoveToBundle(
          ids.filter((id) => bundleOf(id) !== target),
          target
        );
      }
    },
  });

  const assignBundleTarget: AssignBundleTarget | null =
    displayAssignTarget?.type === 'bundle' && ownPlan
      ? (() => {
          const bundle = ownPlan.bundles.find((b) => b.id === displayAssignTarget.id);
          if (!bundle) return null;
          const members = ownPlan.features.filter((f) => f.bundleId === bundle.id);
          // Stored team, else the derived one (see effectiveBundleTeam), so a
          // bundle assigned before teams existed still lists its people.
          const team = effectiveBundleTeam(bundle, members, assignments);
          return {
            id: bundle.id,
            name: bundle.name,
            memberCount: members.length,
            team,
            leaderPartyId: effectiveBundleLeader(bundle, team),
          };
        })()
      : null;
  const assignFeature: DefensePlanFeature | null =
    displayAssignTarget?.type === 'feature'
      ? (ownPlan?.features.find((f) => f.id === displayAssignTarget.id) ?? null)
      : null;
  // Bundle mode gets the links on every member defense, which the form rolls
  // up into one status per teammate.
  const assignLinks: DefensePlanAssignment[] =
    displayAssignTarget?.type === 'feature'
      ? linksForFeature(displayAssignTarget.id, assignments)
      : displayAssignTarget?.type === 'bundle' && ownPlan
        ? ownPlan.features
            .filter((f) => f.bundleId === displayAssignTarget.id)
            .flatMap((f) => linksForFeature(f.id, assignments))
        : [];

  // "Add a defense" region state (see the paletteExpanded doc comment
  // above): an empty plan always shows the full grid; otherwise it's
  // whatever paletteExpanded currently says. Below that, a picked strategy
  // shows as a one-line summary with "Change"; nothing picked on a
  // non-empty plan shows the plain "Add a defense" button instead.
  const isEmptyPlan = !hasDrawnAnything(ownPlan);
  const showExpandedGrid = isEmptyPlan || paletteExpanded;

  // Which of the three states the strategy block is in. When it changes the
  // new content fades in while AnimatedHeight eases the block to its new
  // size. Not on mount: opening the panel should not fade the grid in.
  const paletteStateKey: 'grid' | 'row' | 'button' = showExpandedGrid
    ? 'grid'
    : planDrawTool
      ? 'row'
      : 'button';
  const prevPaletteKeyRef = useRef(paletteStateKey);
  const [fadeKey, setFadeKey] = useState<string | null>(null);
  useEffect(() => {
    const prev = prevPaletteKeyRef.current;
    if (prev === paletteStateKey) return;
    prevPaletteKeyRef.current = paletteStateKey;
    setFadeKey(paletteStateKey);
    // The tile that was pressed left the DOM with the grid, which drops
    // focus to the page. Put it on the row's Change link, unless the user
    // has already moved it somewhere else.
    if (prev === 'grid' && paletteStateKey === 'row' && typeof document !== 'undefined') {
      const active = document.activeElement;
      if (!active || active === document.body) {
        document
          .querySelector<HTMLElement>('[data-testid="plan-strategy-change"]')
          ?.focus({ preventScroll: true });
      }
    }
  }, [paletteStateKey]);

  // "Draw as" only has something to pick between for a ground strategy
  // (line/point/area); a home strategy has exactly one kind, so the control
  // and its label are pointless clutter for those — see kindOptionsForStrategy.
  const kindOptions = planDrawTool ? kindOptionsForStrategy(planDrawTool.strategy) : [];

  const hintKind: 'none' | PlanDrawKind = planDrawTool?.kind ?? 'none';
  // Precedence: a fresh "missed" click beats the steady-state hint for that
  // click's duration, then the zoom-in nudge (homes aren't drawn at this
  // zoom, so clicking wouldn't find one anyway) beats the plain "click a
  // home" text once they are.
  const hintSegments: HintSegment[] =
    (hintKind === 'line' || hintKind === 'area') && showTooShort
      ? TOO_SHORT_HINT
      : hintKind === 'home' && showHomeMiss
        ? HOME_MISS_HINT
        : hintKind === 'home' && !propertyPinsVisible
          ? HOME_ZOOM_HINT
          : HINT_SEGMENTS[hintKind];

  return (
    <View style={styles.content}>
      {/* Fixed header (title/switcher/close + the meta line) and fixed
          footer (visibility + Copy link) bracket a single scrolling middle
          — see the ScrollView further down. Neither region here scrolls;
          styles.content itself never scrolls either, so the two stay put
          while a tall plan's feature list moves underneath them. */}
      {/* Raised as a whole while the switcher is open. The menu hangs out of
          this region and over the palette, which lives in the sibling
          ScrollView below, and a sibling paints later unless this View wins
          on zIndex. Raising styles.header alone is not enough: RN-web gives
          every View its own stacking context, so the header's zIndex only
          orders it against its own siblings inside here. Before this, a
          press on "Duplicate" landed on a strategy tile. */}
      <View
        style={[
          styles.headerFixed,
          { borderBottomColor: colors.border },
          anyMenuOpen && styles.headerRaised,
        ]}>
        {/* Header: editable title (styled as a heading, not a form field),
            plan switcher, close */}
        {/* Raised above the palette while the switcher is open: RN-web gives
            every View its own stacking context, so the menu's own zIndex
            can't lift it over later siblings. */}
        <Text style={[styles.overline, { color: colors.textSecondary }]}>Defense plan</Text>
        <View style={[styles.header, anyMenuOpen && styles.headerRaised]}>
          {ownPlan || (user && !planLoading) ? (
            <View style={styles.titleWrap} {...titleHoverProps}>
              <TextInput
                value={titleDraft}
                onChangeText={setTitleDraft}
                onBlur={() => {
                  setTitleFocused(false);
                  commitTitle();
                }}
                onFocus={() => setTitleFocused(true)}
                onSubmitEditing={commitTitle}
                testID="plan-title-input"
                accessibilityLabel="Plan title"
                style={[
                  styles.titleBox,
                  styles.titleInput,
                  { color: colors.text },
                  (titleHovered || titleFocused) && { backgroundColor: colors.hoverSurface },
                  titleFocused && { borderColor: colors.borderFocus },
                ]}
              />
              {/* Hover-only affordance that this is click-to-rename.
                  Absolutely positioned so it never shifts the title text or
                  the header's width. */}
              {titleHovered && !titleFocused && (
                <View pointerEvents="none" style={styles.titlePencil}>
                  <Pencil size={12} color={colors.textTertiary} />
                </View>
              )}
            </View>
          ) : (
            <Text
              style={[styles.titleBox, styles.titleReadOnly, { color: colors.text }]}
              testID="plan-title-input">
              {unsavedTitle}
            </Text>
          )}
          <View style={styles.switcherAnchor}>
            <Tooltip label="Switch plan">
              <Pressable
                onPress={() => {
                  setActionsOpen(false);
                  setSwitcherOpen((open) => !open);
                }}
                testID="plan-switcher"
                accessibilityRole="button"
                aria-expanded={switcherOpen}
                accessibilityLabel="Switch defense plan"
                style={[styles.switcherButton, CURSOR_POINTER]}>
                <ChevronDown size={HEADER_ICON_SIZE} color={colors.textSecondary} />
              </Pressable>
            </Tooltip>
            {switcherOpen && (
              <>
                {/* accessible={false}: same reasoning as UserMenuDropdown's
                  backdrop — a labeled Pressable wrapping the menu would
                  collapse every row into one opaque element for VoiceOver. */}
                <Pressable
                  style={StyleSheet.absoluteFill}
                  onPress={() => setSwitcherOpen(false)}
                  accessible={false}
                  accessibilityLabel="Close plan switcher"
                />
                <View
                  testID="plan-switcher-menu"
                  style={[
                    styles.switcherMenu,
                    ELEVATION.float,
                    { backgroundColor: colors.background, borderColor: colors.border },
                  ]}>
                  {ownPlans.length === 0 ? (
                    <Text style={[styles.switcherEmpty, { color: colors.textTertiary }]}>
                      No plans yet.
                    </Text>
                  ) : (
                    ownPlans.map((plan) => {
                      const active = plan.id === ownPlan?.id;
                      return (
                        <Pressable
                          key={plan.id}
                          onPress={() => handleSwitchPlan(plan.id)}
                          testID={`plan-switcher-item-${plan.id}`}
                          accessibilityRole="menuitem"
                          aria-checked={active}
                          style={[styles.switcherRow, CURSOR_POINTER]}>
                          <View style={styles.switcherRowText}>
                            <Text
                              style={[styles.switcherRowTitle, { color: colors.text }]}
                              numberOfLines={1}>
                              {plan.title}
                            </Text>
                            <Text style={[styles.switcherRowMeta, { color: colors.textTertiary }]}>
                              {plan.featureCount} {plan.featureCount === 1 ? 'defense' : 'defenses'}
                            </Text>
                          </View>
                          {active && <Check size={16} color={colors.action} />}
                        </Pressable>
                      );
                    })
                  )}
                  <View style={[styles.switcherDivider, { backgroundColor: colors.border }]} />
                  <Pressable
                    onPress={handleNewPlan}
                    testID="plan-new"
                    accessibilityRole="button"
                    style={[styles.switcherRow, CURSOR_POINTER]}>
                    <Plus size={16} color={colors.text} />
                    <Text style={[styles.switcherActionLabel, { color: colors.text }]}>
                      New plan
                    </Text>
                  </Pressable>
                </View>
              </>
            )}
          </View>
          {ownPlan && (
            <View style={styles.switcherAnchor}>
              <Tooltip label="Plan actions">
                <Pressable
                  onPress={() => {
                    setSwitcherOpen(false);
                    setActionsOpen((open) => !open);
                  }}
                  testID="plan-actions"
                  accessibilityRole="button"
                  aria-expanded={actionsOpen}
                  aria-haspopup="menu"
                  accessibilityLabel="Plan actions"
                  style={[styles.switcherButton, CURSOR_POINTER]}>
                  <MoreHorizontal size={HEADER_ICON_SIZE} color={colors.textSecondary} />
                </Pressable>
              </Tooltip>
              {actionsOpen && (
                <>
                  <Pressable
                    style={StyleSheet.absoluteFill}
                    onPress={() => setActionsOpen(false)}
                    accessible={false}
                    accessibilityLabel="Close plan actions"
                  />
                  <View
                    testID="plan-actions-menu"
                    style={[
                      styles.switcherMenu,
                      ELEVATION.float,
                      { backgroundColor: colors.background, borderColor: colors.border },
                    ]}>
                    {hasDrawnAnything(ownPlan) && (
                      <>
                        <Pressable
                          onPress={handleZoomToPlan}
                          testID="plan-zoom-to"
                          accessibilityRole="menuitem"
                          style={[styles.switcherRow, CURSOR_POINTER]}>
                          <Maximize2 size={16} color={colors.text} />
                          <Text style={[styles.switcherActionLabel, { color: colors.text }]}>
                            Zoom to plan
                          </Text>
                        </Pressable>
                        <View
                          style={[styles.switcherDivider, { backgroundColor: colors.border }]}
                        />
                      </>
                    )}
                    <Pressable
                      onPress={handleDuplicatePlan}
                      testID="plan-duplicate"
                      accessibilityRole="menuitem"
                      style={[styles.switcherRow, CURSOR_POINTER]}>
                      <Copy size={16} color={colors.text} />
                      <View style={styles.switcherRowText}>
                        <Text style={[styles.switcherActionLabel, { color: colors.text }]}>
                          Duplicate this plan
                        </Text>
                        <Text style={[styles.switcherRowMeta, { color: colors.textTertiary }]}>
                          Parties aren't copied
                        </Text>
                      </View>
                    </Pressable>
                    <View style={[styles.switcherDivider, { backgroundColor: colors.border }]} />
                    <Pressable
                      onPress={() => void handleDeletePlan()}
                      testID="plan-delete"
                      accessibilityRole="menuitem"
                      style={[styles.switcherRow, CURSOR_POINTER]}>
                      <Trash2 size={16} color={colors.danger} />
                      <Text style={[styles.switcherActionLabel, { color: colors.danger }]}>
                        Delete plan
                      </Text>
                    </Pressable>
                  </View>
                </>
              )}
            </View>
          )}
          <Tooltip label="Close">
            <Pressable
              onPress={closePlanPanel}
              testID="plan-close"
              accessibilityRole="button"
              accessibilityLabel="Close defense plan panel"
              style={[styles.closeButton, CURSOR_POINTER]}>
              <X size={HEADER_ICON_SIZE} color={colors.textSecondary} />
            </Pressable>
          </Tooltip>
        </View>
        <View style={styles.metaRow}>
          {/* "Saving…" fades into the saved line rather than swapping. It
              rides the Crossfade skeleton slot: the saved text mounts once
              saving ends and fades in over it. */}
          <Crossfade
            loading={planLoading || (isSaving && !saveError)}
            style={styles.metaCrossfade}
            skeleton={
              planLoading ? (
                <View style={styles.metaSkeleton}>
                  <Skeleton static width={180} height={12} testID="defense-plan-skeleton-meta" />
                </View>
              ) : (
                <Text style={[styles.metaLine, { color: colors.textSecondary }]} numberOfLines={1}>
                  Saving…
                </Text>
              )
            }>
            <Text
              style={[styles.metaLine, { color: saveError ? colors.danger : colors.textSecondary }]}
              numberOfLines={1}>
              {headerMetaText(ownPlan, isSaving, saveError, assignments)}
            </Text>
          </Crossfade>
          {/* Who can see it, as a pill on the count line (Sept 28): it used
              to take a whole footer block. Hidden until something's drawn,
              like the footer it came from. */}
          {hasDrawnAnything(ownPlan) && ownPlan && (
            <VisibilityPill value={ownPlan.visibility} onChange={handleVisibilityChange} />
          )}
        </View>
        {/* Sept 2026 update loop: reported progress waiting to go out as
            News. Only mounts once there's something unpublished, so a plan
            with no update-loop activity (every existing baseline plan,
            including the audit fixture) renders exactly as before — no
            layout it could push around. */}
        {unpublishedUpdates.length > 0 && (
          <Pressable
            onPress={() => openCompanion({ kind: 'updates' })}
            testID="plan-updates-open"
            accessibilityRole="button"
            accessibilityLabel={`${unpublishedUpdates.length} new update${
              unpublishedUpdates.length === 1 ? '' : 's'
            }`}
            style={[styles.updatesRow, CURSOR_POINTER, { borderColor: colors.border }]}>
            <Bell size={14} color={colors.action} />
            <Text style={[styles.updatesRowText, { color: colors.text }]}>
              New updates ({unpublishedUpdates.length})
            </Text>
            <ChevronRight size={14} color={colors.textTertiary} />
          </Pressable>
        )}
      </View>

      {/* Scrolling middle: "Add a defense", the feature list, "Who's doing
          it" — everything that can grow past the panel's height. The header
          above and the pinned bar / footer below never scroll with it. */}
      <ScrollView
        ref={middleScrollRef}
        style={styles.middleScroll}
        contentContainerStyle={styles.middleContent}
        showsVerticalScrollIndicator={false}>
        {/* "Add a defense": expanded is the tile grid + Draw as + hint —
            rows of 2, equal-width tiles, the last row padded out with
            invisible spacers rather than letting its tiles stretch, so
            a palette whose length isn't a multiple of 2 (11 today) still
            lines up left-aligned under the rows above it. Once a strategy is
            picked (or the panel isn't showing it fresh — see
            paletteExpanded), it collapses to a one-line summary with
            "Change", or to a plain "Add a defense" button when nothing's
            picked and the plan already has something drawn. */}
        {/* Hidden while picking rows for a bundle, so the rows being ticked
            sit at the top of the panel next to the name field (Sept 24
            2026). */}
        {!planBundleSelection && (
          <AnimatedHeight>
            {/* No label over the plain "Add a defense" button: it would repeat
            the button's own words right above it. */}
            {(showExpandedGrid || planDrawTool) && (
              <Text
                style={[
                  styles.sectionLabel,
                  styles.sectionLabelSpaced,
                  styles.sectionLabelFirst,
                  { color: colors.textTertiary },
                ]}
                nativeID="plan-group-label-ground">
                {showExpandedGrid ? 'Landscape' : 'Add a defense'}
              </Text>
            )}
            <View
              key={paletteStateKey}
              // @ts-expect-error dataSet is a react-native-web prop (rediAnim
              // drives the fade-in keyframe in motion.ts); not in RN's types
              dataSet={fadeKey === paletteStateKey ? { rediAnim: 'fade-in' } : undefined}>
              {showExpandedGrid ? (
                <>
                  {STRATEGY_GROUPS.map((group) => (
                    <View key={group.key} testID={`plan-strategy-group-${group.key}`}>
                      {group.key !== 'ground' && (
                        <Text
                          style={[
                            styles.sectionLabel,
                            styles.sectionLabelSpaced,
                            { color: colors.textTertiary },
                          ]}
                          nativeID={`plan-group-label-${group.key}`}>
                          {group.label}
                        </Text>
                      )}
                      <PaletteGrid
                        strategies={group.strategies}
                        selectedStrategy={planDrawTool?.strategy}
                        onPressStrategy={handleStrategyPress}
                        showShortcuts={isDesktopShell}
                        labelledBy={`plan-group-label-${group.key}`}
                      />
                    </View>
                  ))}
                </>
              ) : planDrawTool ? (
                <SelectedStrategyRow
                  strategy={planDrawTool.strategy}
                  kind={planDrawTool.kind}
                  showKind={kindOptions.length <= 1}
                  onChange={() => setPaletteExpanded(true)}
                  onDone={() => setPlanDrawTool(null)}
                />
              ) : (
                <Button
                  title="Add a defense"
                  onPress={() => setPaletteExpanded(true)}
                  variant="secondary"
                  leftIcon={Plus}
                  fullWidth
                  style={styles.addDefenseButton}
                  testID="plan-add-defense"
                />
              )}
            </View>

            {(showExpandedGrid || planDrawTool) && (
              <>
                {/* Line / Point / Area toggle, shown once a strategy is armed and
                it actually has more than one kind to offer (a ground
                strategy) — the strategy sets the kind, this changes it for
                one defense. A home strategy only ever draws as a home, so
                both the label and the control are hidden for those instead
                of showing a single-option toggle with nothing to toggle. */}
                {planDrawTool && kindOptions.length > 1 && (
                  <>
                    {/* A step inside Add a defense, not a new section: a
                        tighter gap than sectionLabelSpaced's 24px. */}
                    <Text
                      style={[
                        styles.sectionLabel,
                        styles.subLabelSpaced,
                        { color: colors.textTertiary },
                      ]}>
                      Draw as
                    </Text>
                    <SegmentedControl
                      options={kindOptions.map((kind) => ({
                        key: kind,
                        label: KIND_LABEL[kind],
                        icon: KIND_ICON[kind],
                      }))}
                      value={planDrawTool.kind}
                      onChange={handleKindPress}
                      ariaLabel="Draw as"
                      testID="plan-kind"
                    />
                  </>
                )}

                {/* One paragraph with inline key caps, so it wraps like text.
                Each cap's following words start with a non-breaking space,
                so a cap never ends a line with its words stranded on the
                next. */}
                {/* Only once a strategy is armed, where it says how to draw
                    (Sept 28): with nothing picked, "Pick a strategy, then
                    draw on the map" repeated the section label and the
                    tiles right above it. */}
                {planDrawTool && (
                  <Text style={[styles.hint, { color: colors.textSecondary }]} testID="plan-hint">
                    {hintSegments.map((seg, i) =>
                      'key' in seg ? (
                        <Text key={i} style={[styles.keyCap, { borderColor: colors.border }]}>
                          {seg.key}
                        </Text>
                      ) : (
                        seg.text
                      )
                    )}
                  </Text>
                )}
              </>
            )}
          </AnimatedHeight>
        )}

        {/* Feature list */}
        <View style={[styles.sectionLabelRow, styles.sectionLabelSpaced]}>
          <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>In this plan</Text>
          {/* Hidden with fewer than 2 loose features — nothing to bundle — and while
              the picker is already open (the sticky bar below takes over). */}
          {ownPlan && looseFeatureCount >= 2 && !planBundleSelection && (
            <TextLink
              label="Make a bundle"
              onPress={handleBundleStart}
              testID="plan-bundle-start"
              textStyle={TYPOGRAPHY.caption}
            />
          )}
          {planBundleSelection && pickableFeatures.length > 0 && (
            <TextLink
              label={allPicked ? 'Clear' : 'Select all'}
              onPress={allPicked ? handleClearPicked : handleSelectAllPickable}
              testID={allPicked ? 'plan-bundle-clear' : 'plan-bundle-select-all'}
              textStyle={TYPOGRAPHY.caption}
            />
          )}
        </View>
        {planBundleSelection && (
          // The name sits with the rows being ticked, not in the bar at the
          // bottom, and it's optional: left blank, the bundle takes the
          // placeholder's name (defaultBundleName). Glenn didn't expect a
          // name to be required and didn't see where it went (Sept 24 2026).
          <View style={styles.bundleNameBlock}>
            <Text style={[styles.bundleNameHint, { color: colors.textSecondary }]}>
              {planBundleEditingId
                ? `Check the defenses that belong in ${editingBundle?.name ?? 'this bundle'}, then save. To dissolve it, use Unbundle.`
                : 'Check the defenses to group, then save.'}
            </Text>
            {!planBundleEditingId && (
              <>
                <Text style={[styles.inputLabel, { color: colors.textSecondary }]}>
                  Bundle name (optional)
                </Text>
                <TextInput
                  value={bundleNameDraft}
                  onChangeText={setBundleNameDraft}
                  onSubmitEditing={handleBundleSave}
                  placeholder={bundleDefaultName || 'Check a defense to start'}
                  placeholderTextColor={colors.textTertiary}
                  maxLength={60}
                  testID="plan-bundle-name"
                  accessibilityLabel="Bundle name (optional)"
                  style={[styles.input, { color: colors.text, borderColor: colors.border }]}
                />
              </>
            )}
          </View>
        )}
        {ownPlan && ownPlan.features.length > 0 ? (
          planBundleSelection ? (
            <View style={styles.featureList}>
              {orderFeaturesNewestFirst(pickableFeatures).map((feature) => (
                <BundleSelectRow
                  key={feature.id}
                  feature={feature}
                  displayLabel={featureLabels[feature.id]}
                  checked={planBundleSelection.includes(feature.id)}
                  onToggle={() => togglePlanBundleSelectionMember(feature.id)}
                />
              ))}
            </View>
          ) : (
            <View
              ref={listDragRef}
              style={styles.featureList}
              // Not a drop target itself: only the strip at its end takes a
              // member out of its bundle (usePlanListDrag.web.ts), so the
              // gaps between rows never read as droppable (Jason, Oct 14).
            >
              {listEntries.map((entry) => {
                if (entry.type === 'feature') {
                  const feature = entry.feature;
                  return (
                    <DefensePlanFeatureRow
                      key={feature.id}
                      feature={feature}
                      displayLabel={featureLabels[feature.id]}
                      dropTarget={dropHighlight === `feature:${feature.id}`}
                      selected={isPlanFeatureSelected(
                        feature.id,
                        selectedPlanFeatureId,
                        planMultiSelection
                      )}
                      tabbable={feature.id === rovingRowId}
                      animateIn={justDrawnIds.has(feature.id)}
                      active={activeAssignFeatureId === feature.id}
                      onSelect={(e) => handleRowPress(feature.id, e)}
                      onDelete={() => handleDelete(feature)}
                      onCopyAs={(strategy) => handleCopyAs(feature, strategy)}
                      menu={rowMenuFor(feature)}
                      who={{
                        links: linksForFeature(feature.id, assignments),
                        parties,
                        assignShortcutLive: assignKeyLive,
                        onAssign: canWriteAssignments
                          ? () =>
                              openCompanion({
                                kind: 'assign',
                                target: { type: 'feature', id: feature.id },
                              })
                          : undefined,
                      }}
                    />
                  );
                }
                const { bundle, members } = entry;
                const expanded = resolveBundleExpanded(bundle.id, members.length);
                const bundleSelected = selectedPlanBundleId === bundle.id;
                return (
                  <DefensePlanBundleGroup
                    key={bundle.id}
                    dropId={bundle.id}
                    dropTarget={dropHighlight === `bundle:${bundle.id}`}
                    selected={bundleSelected}
                    expanded={expanded}
                    header={
                      <DefensePlanBundleRow
                        bundle={bundle}
                        members={members}
                        selected={bundleSelected}
                        active={
                          activeAssignBundleId === bundle.id ||
                          eventsBundleId === bundle.id ||
                          bidsBundleId === bundle.id
                        }
                        expanded={expanded}
                        onToggleExpanded={() => toggleBundleExpanded(bundle.id, members.length)}
                        onSelectHeader={() => handleBundleHeaderSelect(bundle.id)}
                        who={{
                          text: bundleRowWhoText(bundle, members, assignments, parties),
                          onAssign: canWriteAssignments
                            ? () =>
                                openCompanion({
                                  kind: 'assign',
                                  target: { type: 'bundle', id: bundle.id },
                                })
                            : undefined,
                        }}
                        onRename={(name) => void renameBundle(bundle.id, name)}
                        onEditMembers={() => handleBundleEdit(bundle.id)}
                        onUngroup={() => void handleUngroup(bundle.id)}
                        nextEvent={nextEventForBundle(
                          planEvents ?? [],
                          ownPlan?.id ?? '',
                          bundle.id
                        )}
                        onScheduleEvent={() =>
                          openCompanion({ kind: 'events', bundleId: bundle.id })
                        }
                        planId={ownPlan?.id}
                        bidCount={(planBids ?? []).filter((b) => b.bundleId === bundle.id).length}
                        onOpenBids={() => openCompanion({ kind: 'bids', bundleId: bundle.id })}
                        onFindVendors={() =>
                          openCompanion({ kind: 'vendors', bundleId: bundle.id })
                        }
                        onToggleBids={() => void handleToggleBids(bundle.id)}
                        onCopyOpportunityLink={() => void handleCopyOpportunityLink(bundle.id)}
                        fundingText={bundleFundingText(
                          (planFundingRequests ?? []).filter((r) => r.bundleId === bundle.id)
                        )}
                        onToggleWorkDone={() => void handleToggleWorkDone(bundle.id)}
                      />
                    }>
                    {members.map((feature) => (
                      <DefensePlanFeatureRow
                        nested
                        key={feature.id}
                        feature={feature}
                        displayLabel={featureLabels[feature.id]}
                        selected={isPlanFeatureSelected(
                          feature.id,
                          selectedPlanFeatureId,
                          planMultiSelection
                        )}
                        tabbable={feature.id === rovingRowId}
                        active={activeAssignFeatureId === feature.id}
                        onSelect={(e) => handleRowPress(feature.id, e)}
                        onDelete={() => handleDelete(feature)}
                        onCopyAs={(strategy) => handleCopyAs(feature, strategy)}
                        menu={rowMenuFor(feature)}
                        who={{
                          links: linksForFeature(feature.id, assignments),
                          parties,
                          assignShortcutLive: assignKeyLive,
                          onAssign: canWriteAssignments
                            ? () =>
                                openCompanion({
                                  kind: 'assign',
                                  target: { type: 'feature', id: feature.id },
                                })
                            : undefined,
                        }}
                      />
                    ))}
                  </DefensePlanBundleGroup>
                );
              })}
              {/* While a bundle member is being dragged: a strip to drop it
                  on. Without it, a plan whose every defense sits in one
                  bundle has no loose gap left to drop into (Oct 14 2026
                  browser check), so "take out" could only be done from the
                  ⋯ menu. */}
              {dragFromBundle && (
                <View
                  testID="plan-loose-drop-zone"
                  style={[
                    styles.looseDropZone,
                    { borderColor: looseTargeted ? colors.borderFocus : colors.border },
                  ]}
                  // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
                  dataSet={{ planDrop: 'loose' }}>
                  {/* The fill is a child inset inside the dashed border, the way the
                      bundle card's drop fill is: a background on the bordered box
                      itself paints under the border and shows through the dash
                      gaps as a sliver above the outline (Jason, Oct 14). */}
                  {looseTargeted && (
                    <View
                      pointerEvents="none"
                      style={[styles.looseDropFill, { backgroundColor: colors.hoverSurface }]}
                    />
                  )}
                  <Text style={[styles.looseDropText, { color: colors.textSecondary }]}>
                    Drop here to take it out of its bundle
                  </Text>
                </View>
              )}
            </View>
          )
        ) : (
          <Crossfade
            loading={planLoading}
            skeleton={
              <View style={styles.featureList} testID="defense-plan-skeleton">
                {[0, 1, 2].map((i) => (
                  <View key={i} style={styles.skeletonRow}>
                    <Skeleton static width={20} height={20} borderRadius={BORDER_RADIUS.full} />
                    <View style={styles.skeletonRowText}>
                      <Skeleton static width={i === 1 ? '45%' : '60%'} height={14} />
                      <Skeleton static width="35%" height={12} />
                    </View>
                  </View>
                ))}
              </View>
            }>
            <Text style={[styles.emptyState, { color: colors.textTertiary }]}>
              Nothing drawn yet.
            </Text>
          </Crossfade>
        )}

        {Object.keys(parties).length > 0 && (
          <PlanPartiesSection
            planId={ownPlan?.id ?? null}
            parties={parties}
            assignments={assignments}
            onEditParty={(partyId) => openCompanion({ kind: 'party', partyId })}
            activePartyId={editPartyId}
          />
        )}
      </ScrollView>

      {/* Pinned just above the footer, never scrolling with the middle: the
          bundle-naming sticky bar while picking. */}
      {planBundleSelection && (
        <View style={styles.pinnedBar}>
          {planBundleSelection && (
            // Takes the footer's place while picking, in the footer's box and
            // button size (Sept 28): the count, then two equal sm buttons
            // sharing the rest of the one row.
            <View style={[styles.bundleBar, { borderTopColor: colors.border }]}>
              <Text style={[styles.bundleBarCount, { color: colors.textSecondary }]}>
                {pickedIds.length} selected
              </Text>
              <View style={[styles.footerActions, styles.bundleBarActions]}>
                <Button
                  title="Cancel"
                  onPress={handleBundleCancel}
                  variant="secondary"
                  size="sm"
                  testID="plan-bundle-cancel"
                  style={styles.footerAction}
                />
                <Button
                  title={planBundleEditingId ? 'Save' : 'Save bundle'}
                  onPress={handleBundleSave}
                  variant="primary"
                  disabled={pickedIds.length === 0}
                  size="sm"
                  testID="plan-bundle-save"
                  style={styles.footerAction}
                />
              </View>
            </View>
          )}
        </View>
      )}

      {/* A selection of 2 or more (Cmd/Ctrl/Shift+click) takes the footer's
          place, as the picker bar does: the count on its own line, then four
          equal buttons. */}
      {hasSelection && !planBundleSelection && (
        <View style={styles.pinnedBar} testID="plan-selection-bar">
          <View style={[styles.selectionBar, { borderTopColor: colors.border }]}>
            <Text
              style={[styles.bundleBarCount, { color: colors.textSecondary }]}
              testID="plan-selection-count">
              {selectionIds.length} selected
            </Text>
            <View style={styles.footerActions}>
              {selectionSharesBundle ? (
                <Tooltip label="Already bundled">{bundleButton}</Tooltip>
              ) : (
                bundleButton
              )}
              <View ref={selectionAddRef} style={styles.footerAction}>
                <Button
                  title="Add to…"
                  onPress={() => setSelectionAddOpen((open) => !open)}
                  variant="secondary"
                  size="sm"
                  disabled={(ownPlan?.bundles.length ?? 0) === 0}
                  testID="plan-selection-add"
                  accessibilityLabel={`Add ${selectionIds.length} selected to a bundle`}
                  style={styles.selectionActionInner}
                />
              </View>
              <Button
                title="Delete"
                onPress={() => void handleDeleteMany([...selectionIds])}
                variant="secondary"
                size="sm"
                testID="plan-selection-delete"
                accessibilityLabel={`Delete ${selectionIds.length} selected`}
                style={styles.selectionAction}
              />
              <Button
                title="Clear"
                onPress={clearPlanMultiSelection}
                variant="secondary"
                size="sm"
                testID="plan-selection-clear"
                accessibilityLabel="Clear the selection"
                style={styles.selectionAction}
              />
            </View>
          </View>
          <Popover
            visible={selectionAddOpen}
            anchorRef={selectionAddRef}
            onClose={() => setSelectionAddOpen(false)}
            placement="top-end"
            gap={SPACING.xxs}
            testID="plan-selection-add-menu"
            accessibilityLabel="Add the selection to a bundle">
            <View style={styles.selectionMenu}>
              <BundleListMenu
                bundles={(ownPlan?.bundles ?? []).map(({ id, name }) => ({ id, name }))}
                labelFor={(b) => b.name}
                testIDFor={(b) => `plan-selection-add-${b.id}`}
                scrollTestID="plan-selection-add-bundles"
                onPick={(b) => {
                  setSelectionAddOpen(false);
                  void handleMoveToBundle([...selectionIds], b.id);
                  clearPlanMultiSelection();
                }}
              />
            </View>
          </Popover>
        </View>
      )}

      {/* Fixed footer: share and the needs list (visibility moved up to the
          header's count line, Sept 28). Hidden for a 0-feature
          plan (its last feature just got deleted, the doc is kept for undo)
          — nothing to share yet, same as before the first defense was ever
          drawn. */}
      {hasDrawnAnything(ownPlan) && ownPlan && !planBundleSelection && !hasSelection && (
        <View style={[styles.footer, { borderTopColor: colors.border }]}>
          {/* One row, not two stacked full-width buttons: stacked, the
              footer took ~200px and pushed the list off a 720px screen
              (Sept 24 2026). */}
          <View style={styles.footerActions}>
            {/* The incoming half fades in when the visibility flips (Crossfade
                is skeleton-to-content only, so this reuses the panel's
                fade-in keyframe instead). Keyed on the state so it remounts. */}
            <View
              key={ownPlan.visibility}
              style={styles.footerAction}
              // @ts-expect-error dataSet is a react-native-web prop; not in RN's type defs.
              dataSet={footerSwapped ? { rediAnim: 'fade-in' } : undefined}>
              {ownPlan.visibility === 'shared' ? (
                <Button
                  title={justCopied ? 'Link copied' : 'Copy link'}
                  onPress={() => void handleShare()}
                  variant="primary"
                  leftIcon={Link}
                  size="sm"
                  testID="plan-share"
                  accessibilityLabel="Copy plan link"
                  style={styles.footerAction}
                />
              ) : (
                // A private plan has no link to copy. Say so where the button
                // would be, with the way out, instead of the button vanishing
                // and the footer changing shape (Sept 28).
                <View style={[styles.footerAction, styles.privateNote]} testID="plan-private-note">
                  <Lock size={14} color={colors.textSecondary} />
                  <View style={styles.privateNoteText}>
                    <Text style={[styles.privateNoteLine, { color: colors.textSecondary }]}>
                      Private ·
                    </Text>
                    <TextLink
                      label="Share it"
                      onPress={() => void handleVisibilityChange('shared')}
                      testID="plan-private-share"
                      accessibilityLabel="Share this plan to get a link"
                      textStyle={TYPOGRAPHY.caption}
                    />
                  </View>
                </View>
              )}
            </View>
            {/* The needs list, for talking to vendors (client ask, Sept 2026).
                Web only: there is no browser download on native. */}
            {isDownloadSupported() && (
              <Button
                title="Needs list"
                onPress={handleDownloadNeeds}
                variant="secondary"
                leftIcon={Download}
                size="sm"
                testID="plan-needs-csv"
                accessibilityLabel="Download the needs list as a spreadsheet"
                style={styles.footerAction}
              />
            )}
          </View>
        </View>
      )}

      <PlanDragGhost nodeRef={dragGhostRef} />
      <PlanFeatureContextMenu
        menuFor={rowMenuFor}
        onCopyAs={handleCopyAs}
        onDelete={handleDelete}
      />

      {/* Assign, edit-party, plan updates and bundle events: companions of this panel on
          the desktop shell (CLAUDE.md's "Panels, not dialogs" — never stack
          a dialog on a docked panel), an AdaptiveSheet everywhere else. Only
          one of them is ever open at once (the `companion` state
          above), so at most one of these blocks renders anything. */}
      {isDesktopShell ? (
        <>
          {companionLeave.mounted && shownCompanion?.kind === 'assign' && (
            <CompanionPanel
              shown={companionLeave.shown}
              onExited={companionLeave.onExited}
              attachTo="defense-plan-panel"
              parentWidth={PLAN_PANEL_WIDTH}
              folded={desktopCompanionFolded}
              overline="Assign"
              title={assignTargetLabel(
                assignFeature,
                assignBundleTarget,
                assignFeature ? featureLabels[assignFeature.id] : undefined
              )}
              subtitle="Who's responsible?"
              onClose={closeCompanionAndPop}>
              <DefensePlanAssignForm
                planId={ownPlan?.id ?? null}
                feature={assignFeature}
                featureLabel={assignFeature ? featureLabels[assignFeature.id] : undefined}
                objectInHeader
                bundle={assignBundleTarget}
                parties={parties}
                links={assignLinks}
                areaId={ownPlan?.areaId ?? null}
                onClose={closeCompanionAndPop}
                historyEnabled
              />
            </CompanionPanel>
          )}
          {companionLeave.mounted && shownCompanion?.kind === 'party' && (
            <CompanionPanel
              shown={companionLeave.shown}
              onExited={companionLeave.onExited}
              attachTo="defense-plan-panel"
              parentWidth={PLAN_PANEL_WIDTH}
              folded={desktopCompanionFolded}
              overline="Edit party"
              title={
                parties[shownCompanion.partyId]
                  ? partyDisplayName(parties[shownCompanion.partyId])
                  : 'Unknown party'
              }
              onClose={closeCompanionAndPop}>
              <DefensePlanPartyEditForm
                planId={ownPlan?.id ?? null}
                party={parties[shownCompanion.partyId] ?? null}
                hideTitle
                onClose={closeCompanionAndPop}
              />
            </CompanionPanel>
          )}
          {companionLeave.mounted && shownCompanion?.kind === 'updates' && (
            <CompanionPanel
              shown={companionLeave.shown}
              onExited={companionLeave.onExited}
              attachTo="defense-plan-panel"
              parentWidth={PLAN_PANEL_WIDTH}
              folded={desktopCompanionFolded}
              overline="New updates"
              title={ownPlan?.title || 'This plan'}
              onClose={closeCompanionAndPop}>
              <PlanUpdatesView
                planId={ownPlan?.id ?? null}
                onClose={closeCompanionAndPop}
                hideTitle
              />
            </CompanionPanel>
          )}
          {companionLeave.mounted && shownCompanion?.kind === 'events' && ownPlan && (
            <CompanionPanel
              shown={companionLeave.shown}
              onExited={companionLeave.onExited}
              attachTo="defense-plan-panel"
              parentWidth={PLAN_PANEL_WIDTH}
              folded={desktopCompanionFolded}
              overline="Events"
              title={
                ownPlan.bundles.find((b) => b.id === shownCompanion.bundleId)?.name ?? 'Bundle'
              }
              subtitle="A place and a time for the work"
              onClose={closeCompanionAndPop}>
              <PlanEventsPanel planId={ownPlan.id} bundleId={shownCompanion.bundleId} hideTitle />
            </CompanionPanel>
          )}
          {companionLeave.mounted && shownCompanion?.kind === 'bids' && ownPlan && (
            <CompanionPanel
              shown={companionLeave.shown}
              onExited={companionLeave.onExited}
              attachTo="defense-plan-panel"
              parentWidth={PLAN_PANEL_WIDTH}
              folded={desktopCompanionFolded}
              overline="Vendor bids"
              title={
                ownPlan.bundles.find((b) => b.id === shownCompanion.bundleId)?.name ?? 'Bundle'
              }
              subtitle="Vendors who can help with this work"
              onClose={closeCompanionAndPop}>
              <DefensePlanBidsView
                planId={ownPlan.id}
                bundleId={shownCompanion.bundleId}
                hideTitle
                onFindVendors={() =>
                  openCompanion({ kind: 'vendors', bundleId: shownCompanion.bundleId })
                }
              />
            </CompanionPanel>
          )}
          {companionLeave.mounted && shownCompanion?.kind === 'vendors' && ownPlan && (
            <CompanionPanel
              shown={companionLeave.shown}
              onExited={companionLeave.onExited}
              attachTo="defense-plan-panel"
              parentWidth={PLAN_PANEL_WIDTH}
              folded={desktopCompanionFolded}
              overline="Find vendors"
              title={
                ownPlan.bundles.find((b) => b.id === shownCompanion.bundleId)?.name ?? 'Bundle'
              }
              subtitle="Pick vendors to invite to bid"
              onClose={closeCompanionAndPop}>
              <BundleVendorsView
                planId={ownPlan.id}
                bundleId={shownCompanion.bundleId}
                highlightVendorIds={shownCompanion.highlightVendorIds}
                hideTitle
              />
            </CompanionPanel>
          )}
        </>
      ) : (
        <>
          <DefensePlanAssignSheet
            visible={assignTarget != null}
            planId={ownPlan?.id ?? null}
            feature={assignFeature}
            bundle={assignBundleTarget}
            parties={parties}
            links={assignLinks}
            areaId={ownPlan?.areaId ?? null}
            onClose={closeCompanion}
          />
          <DefensePlanPartyEditSheet
            visible={editPartyId != null}
            planId={ownPlan?.id ?? null}
            party={editPartyId ? (parties[editPartyId] ?? null) : null}
            onClose={closeCompanion}
          />
          <PlanUpdatesSheet
            visible={updatesOpen}
            planId={ownPlan?.id ?? null}
            onClose={closeCompanion}
          />
          <PlanEventsSheet
            visible={eventsBundleId != null}
            planId={ownPlan?.id ?? null}
            bundleId={eventsBundleId}
            onClose={closeCompanion}
          />
          <DefensePlanBidsSheet
            visible={bidsBundleId != null}
            planId={ownPlan?.id ?? null}
            bundleId={bidsBundleId}
            onClose={closeCompanion}
            onFindVendors={(bundleId) => openCompanion({ kind: 'vendors', bundleId })}
          />
          <DefensePlanVendorsSheet
            visible={vendorsBundleId != null}
            planId={ownPlan?.id ?? null}
            bundleId={vendorsBundleId}
            onClose={closeCompanion}
          />
        </>
      )}
    </View>
  );
}

const HEADER_ICON_SIZE = 18;
const HEADER_BUTTON_SIZE = SPACING.lg + SPACING.xs;

const styles = StyleSheet.create({
  // Fills the DockedPanel's own inner ScrollView viewport exactly (that
  // ScrollView's contentContainerStyle is flexGrow:1 — see DockedPanel.tsx)
  // rather than scrolling itself, so a flex:1 child here (middleScroll)
  // lays out to the panel's real height and handles its own overflow,
  // leaving header/pinnedBar/footer in place regardless of how long the
  // feature list gets.
  content: {
    flex: 1,
  },
  // A hairline closes the header off from the scrolling body, the way the
  // prototype's plan header does (Sept 28).
  headerFixed: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.smd,
    borderBottomWidth: StyleSheet.hairlineWidth,
    minHeight: PLAN_PANEL_HEADER_HEIGHT,
  },
  middleScroll: {
    flex: 1,
  },
  middleContent: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.sm,
  },
  pinnedBar: {
    paddingHorizontal: SPACING.md,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.sm,
  },
  headerRaised: {
    zIndex: 10,
  },
  overline: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xxs,
  },
  // Shared box geometry for both the read-only title (no plan yet) and the
  // editable title (a plan exists), so the text sits in the exact same spot
  // either way — the negative margin compensates for the padding added for
  // the hover/focus box, so at rest the text lines up with the rest of the
  // header.
  titleBox: {
    ...TYPOGRAPHY.h4,
    flex: 1,
    paddingVertical: SPACING.xxs,
    paddingHorizontal: SPACING.xs,
    marginLeft: -SPACING.xs,
    marginVertical: -SPACING.xxs,
    borderRadius: BORDER_RADIUS.sm,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  titleInput: {},
  titleReadOnly: {},
  titleWrap: {
    flex: 1,
    position: 'relative',
  },
  // Sits inside the title box's own right padding, vertically centered —
  // titleBox already reserves SPACING.xs on that edge, so this can't
  // overlap the switcher/close buttons past it.
  titlePencil: {
    position: 'absolute',
    right: SPACING.xs,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  // The switcher and close share one square, centered on the title line
  // (they were 20 and 22px, 2-3px above the title's center; Sept 24 2026).
  closeButton: {
    width: HEADER_BUTTON_SIZE,
    height: HEADER_BUTTON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: BORDER_RADIUS.full,
    marginRight: -SPACING.xs,
  },
  switcherAnchor: {
    position: 'relative',
  },
  switcherButton: {
    width: HEADER_BUTTON_SIZE,
    height: HEADER_BUTTON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: BORDER_RADIUS.full,
  },
  switcherMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: SPACING.xxs,
    width: 240,
    borderRadius: BORDER_RADIUS.md,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xs,
    zIndex: 10,
  },
  switcherEmpty: {
    ...TYPOGRAPHY.caption,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.xxs,
  },
  switcherRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.xxs,
  },
  switcherRowText: {
    flex: 1,
  },
  switcherRowTitle: {
    ...TYPOGRAPHY.label,
  },
  switcherRowMeta: {
    ...TYPOGRAPHY.caption,
  },
  switcherActionLabel: {
    ...TYPOGRAPHY.label,
    flex: 1,
  },
  switcherDivider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: SPACING.xs,
  },
  // Tight under the name (Sept 28): the header row is already taller than
  // the title because of its 28px buttons, so no extra margin on top.
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  visibilityPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xxs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.full,
    paddingHorizontal: SPACING.xs + SPACING.xxs,
    paddingVertical: 1,
  },
  visibilityPillLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
  },
  visibilityMenu: {
    padding: SPACING.xs,
    width: 260,
  },
  visibilityMenuRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
  },
  visibilityMenuText: {
    flex: 1,
  },
  metaLine: {
    ...TYPOGRAPHY.metadata,
    fontVariant: ['tabular-nums'],
    marginTop: 0,
    minHeight: TYPOGRAPHY.metadata.lineHeight,
  },
  updatesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    marginTop: SPACING.sm,
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
  },
  updatesRowText: {
    ...TYPOGRAPHY.label,
    flex: 1,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
  },
  sectionLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // One rhythm for every section in the scrolling body (Sept 28): 24px
  // above a section label, 8px from the label to what it labels. The first
  // section drops the top margin; the body's own top padding covers it.
  sectionLabelSpaced: {
    marginTop: SPACING.lg,
    marginBottom: SPACING.sm,
  },
  sectionLabelFirst: {
    marginTop: 0,
  },
  subLabelSpaced: {
    marginTop: SPACING.smd,
    marginBottom: SPACING.sm,
  },
  // Stands where the "Add a defense" label sits otherwise, first in the
  // body, so no top margin either.
  addDefenseButton: {
    marginTop: 0,
  },
  // No top margin of its own: sectionLabelSpaced's marginBottom (8) is the
  // only gap between the label and the first tile (Sept 29 2026).
  palette: {
    gap: SPACING.xs,
    marginTop: 0,
  },
  paletteRow: {
    flexDirection: 'row',
    gap: SPACING.xs,
  },
  tile: {
    ...STRATEGY_TILE_BOX,
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  // Takes the tile's own box (the tile style comes first) so an incomplete
  // last row keeps its columns: a bare flex:1 spacer has no border or
  // padding, so it came out 6px narrower and pushed the row's tiles right.
  paletteSpacer: {
    borderColor: 'transparent',
  },
  // Icon only, in the strategy's color (Sept 28): the outlined tile already
  // frames it, so a tinted circle inside was a second frame. The slot keeps
  // the old badge size so nothing moves; feature rows and the Key keep
  // their circles, which match the map's markers.
  strategyBadge: {
    width: STRATEGY_BADGE_SIZE,
    height: STRATEGY_BADGE_SIZE,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Two lines of caption fit inside the badge's height, so a wrapped label
  // never makes its tile taller than its neighbor.
  tileLabel: {
    ...TYPOGRAPHY.caption,
    flex: 1,
    minWidth: 0,
  },
  // SelectedStrategyRow — the "Add a defense" region collapsed with a
  // strategy picked: badge + label on the left, "Change" on the right.
  selectedStrategyRow: {
    ...STRATEGY_TILE_BOX,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.sm,
  },
  selectedStrategyLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    flexShrink: 1,
  },
  selectedStrategyLabel: {
    ...TYPOGRAPHY.label,
    flexShrink: 1,
  },
  selectedStrategyActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  hint: {
    ...TYPOGRAPHY.caption,
    marginTop: SPACING.sm,
  },
  keyCap: {
    ...TYPOGRAPHY.caption,
    paddingHorizontal: SPACING.xxs,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.xs,
  },
  featureList: {
    gap: SPACING.xs,
  },
  // The footer's box (see footer below), less its side padding, which
  // pinnedBar already supplies.
  bundleBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: SPACING.md,
    paddingBottom: SPACING.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.md,
  },
  bundleBarCount: {
    ...TYPOGRAPHY.caption,
  },
  // The selection bar: the picker bar's box, but the count sits above the
  // buttons, because four buttons and a count do not share a 328px row.
  selectionBar: {
    paddingTop: SPACING.md,
    paddingBottom: SPACING.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.sm,
  },
  // Four sm buttons split 328px into 76px each; the default 12px side
  // padding leaves "Add to…" too little room, so these take 8.
  selectionAction: {
    flex: 1,
    paddingHorizontal: SPACING.xs,
  },
  // The same button inside a View that already takes the flex share.
  selectionActionInner: {
    paddingHorizontal: SPACING.xs,
  },
  selectionMenu: {
    minWidth: 200,
    paddingVertical: SPACING.xs,
  },
  bundleBarActions: {
    flex: 1,
  },
  // The bundle picker's header, spaced and styled like the Assign form's
  // fields (Sept 28): instruction, 12px, label and input 4px apart, then
  // 16px before the rows.
  bundleNameBlock: {
    marginBottom: SPACING.md,
  },
  bundleNameHint: {
    ...TYPOGRAPHY.caption,
    marginBottom: SPACING.smd,
  },
  inputLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
    marginBottom: SPACING.xs,
  },
  input: {
    ...TYPOGRAPHY.bodySmall,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.sm,
  },
  // BundleSelectRow — mirrors DefensePlanFeatureRow.web.tsx's row/badge/
  // text/label/meta styles so a select-mode row reads the same, with a
  // checkbox where the delete control would be.
  // Plain rows like the list they stand in for (Sept 28): no box, the
  // checked state is the pressed fill plus the filled checkbox.
  selectRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: BORDER_RADIUS.xs,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    width: 22,
    height: 22,
    borderRadius: BORDER_RADIUS.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    flex: 1,
  },
  label: {
    ...TYPOGRAPHY.label,
  },
  meta: {
    ...TYPOGRAPHY.caption,
    marginTop: 1,
  },
  emptyState: {
    ...TYPOGRAPHY.caption,
  },
  // The "take it out" strip under the list while a member is dragged: a
  // dashed box the height of a loose row, so the ghost has somewhere to go.
  looseDropZone: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: BORDER_RADIUS.md,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.sm,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  looseDropFill: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: BORDER_RADIUS.md - 1,
  },
  looseDropText: {
    ...TYPOGRAPHY.caption,
  },
  // Same box as DefensePlanFeatureRow's loose row (rowCard padding, 20px
  // badge, a title line and a meta line).
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.xs,
    paddingLeft: SPACING.sm,
    paddingRight: SPACING.sm - 1,
    paddingVertical: SPACING.sm,
  },
  skeletonRowText: {
    flex: 1,
    minWidth: 0,
    gap: SPACING.xxs,
  },
  metaCrossfade: {
    flexShrink: 1,
  },
  metaSkeleton: {
    minHeight: TYPOGRAPHY.metadata.lineHeight,
    justifyContent: 'center',
  },
  footer: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.lg,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.md,
  },
  footerActions: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  footerAction: {
    flex: 1,
  },
  privateNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  // One line: "Private · Share it" (Sept 28, Jason: two lines read as
  // a wrap).
  privateNoteText: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    flexShrink: 1,
  },
  privateNoteLine: {
    ...TYPOGRAPHY.caption,
  },
});
